import { Formik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import {
  FiChevronLeft,
  FiChevronRight,
  FiPaperclip,
  FiPlus,
  FiRefreshCw,
} from "react-icons/fi";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import activeLoadStatusIcon from "../../../assets/images/Active_load.svg";
import inactiveStatusIcon from "../../../assets/images/Inactive_icon.svg";
import statusDecommissionedIcon from "../../../assets/images/Status Decommissioned.svg";
import statusUnderMaintenanceIcon from "../../../assets/images/Status Under Maintenance.svg";
import AssetLinkField from "../../../components/AssetLinkField";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CnicInput from "../../../components/CnicInput";
import CustomModal from "../../../components/CustomModal";
import CustomTabs from "../../../components/CustomTabs";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import LogsComments from "../../../components/LogsComments";
import NewTableComponent from "../../../components/NewTableComponent";
import PhoneNumberInput from "../../../components/PhoneNumberInput";
import Select from "../../../components/Select";
import UserAvatar from "../../../components/UserAvatar";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Services.css";
import "../../../styles/Trip.css";
import { apiService } from "../../../services/apiService";
import {
  clearVehicleDropdownState,
  fetchVehicleCompanyBranchesDropdown,
  fetchVehicleMakesDropdown,
  fetchVehicleModelsDropdown,
  fetchVehicleTransmissionTypesDropdown,
} from "../../../store/Slices/VehicleDropdownSlice";
import {
  fetchBanksDropdown,
  fetchThreePlTransporterDropdown,
  fetchTransporterVehicleTypesDropdown,
  fetchVehiclesDropdown,
} from "../../../store/Slices/dropdownSlice";
import {
  clearVehicleSingleDetailState,
  clearVehicleHistoryState,
  clearVehicleInstallmentsState,
  clearVehicleOperationalDataState,
  clearVehicleServiceRecordsState,
  attachVehicleEquipment,
  attachVehicleTyres,
  commentOnVehicle,
  createVehicleInstallment,
  detachVehicleTyre,
  detachVehicleEquipment,
  fetchVehicleAssignmentHistory,
  fetchVehicleDistanceSummary,
  fetchVehicleInstallments,
  fetchVehicleSingleDetail,
  fetchVehicleHistory,
  fetchVehicleFuelLogs,
  fetchVehicleServiceRecords,
  fetchVehicleTrips,
  updateVehicleGeneralInfo,
  updateVehiclePurchaseInfo,
} from "../../../store/Slices/ServiceSlice";
import { fetchEquipmentDropdown } from "../../../store/Slices/EquipmentSlice";
import { getFuelStations } from "../../../store/Slices/FuelSlice";
import {
  AppMainModules,
  AssetsPermission,
  AssetsSubModules,
} from "../../../utils/app-constant";
import {
  findCollectionByKeys,
  formats,
  getCurrentUser,
  hasPermission,
  modules,
  normalizeOptions,
} from "../../../utils/helper";
import { buildCnicSchema } from "../../../utils/pakistanCnic";
import {
  checkedIcon,
  closedIcon,
  inProgressIcon,
  linkedIcon,
  openIcon,
  pendingIcon,
} from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import { buildPhoneNumberSchema } from "../../../pages/ThreePL/shared/phoneValidation";
import {
  coverageTypeOptions,
  depreciationMethodOptions,
  fuelTypeOptions,
  insuranceProviderOptions,
  paymentMethodOptions,
  paymentTypeOptions,
  trackingPackageOptions,
  vehicleStatusOptions,
  vehicleTypeOptions,
} from "./vehicleAssets.constants";
import { fetchAssetTyres, fetchTyresDropdown } from "../../../store/Slices/TyreSlice";

const normalizeAssetsRegistrationTab = (value, fallback = "vehicles") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "vehicles") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "vehicles") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};



const getPositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};


const formatAmount = (value) => normalizeNumber(value, 0).toLocaleString("en-US");

const formatDateOnly = (value) => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) return rawValue;

  const parsedDate = new Date(rawValue);
  if (Number.isNaN(parsedDate.getTime())) return rawValue.slice(0, 10);

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeUploadedFiles = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map(
      (file) =>
        file?.fileName ||
        file?.file_name ||
        file?.file_path ||
        file?.path ||
        file?.url ||
        file?.name ||
        file
    )
    .filter(Boolean);

const extractVehicleFiles = (source) => {
  if (!source) return [];
  if (Array.isArray(source)) return source.filter(Boolean);
  if (typeof source !== "object") return [];
  if (Array.isArray(source.files) && source.files.length) return source.files;
  if (Array.isArray(source.attachments) && source.attachments.length) return source.attachments;
  if (Array.isArray(source.documents) && source.documents.length) return source.documents;
  if (Array.isArray(source.driverDocument) && source.driverDocument.length) return source.driverDocument;
  if (Array.isArray(source.driver_documents) && source.driver_documents.length) return source.driver_documents;
  if (Array.isArray(source.vehicle_files) && source.vehicle_files.length) return source.vehicle_files;
  if (Array.isArray(source.vehicle_3pl?.files) && source.vehicle_3pl.files.length) return source.vehicle_3pl.files;
  if (Array.isArray(source.vehicle_3pl?.documents) && source.vehicle_3pl.documents.length) return source.vehicle_3pl.documents;
  if (Array.isArray(source.vehicle_hired?.files) && source.vehicle_hired.files.length) return source.vehicle_hired.files;
  if (Array.isArray(source.formData?.attachments) && source.formData.attachments.length) return source.formData.attachments;
  if (Array.isArray(source.formData?.files) && source.formData.files.length) return source.formData.files;
  return [];
};

const normalizeInstallmentFiles = (files = []) => normalizeUploadedFiles(files);

const isFullPaymentType = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_") === "full_payment";

const getInstallmentAttachmentPath = (file) => {
  if (!file) return "";
  if (typeof file === "string") return file;
  return (
    file?.file_path ||
    file?.fileName ||
    file?.file_name ||
    file?.path ||
    file?.url ||
    ""
  );
};

const buildInstallmentAttachmentUrl = (filePath = "") => {
  const value = String(filePath || "").trim();
  if (!value) return "";
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  return `${process.env.REACT_APP_API_FILES_URL}${value}`;
};

const getInstallmentAttachmentName = (url = "") => {
  const cleanUrl = String(url || "").split("?")[0];
  const name = cleanUrl.split("/").pop() || "Attachment";
  try {
    return decodeURIComponent(name);
  } catch {
    return name;
  }
};

const isImageAttachment = (url = "") =>
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(String(url).split("?")[0]);

const resolveInstallmentAttachmentUrls = (files = []) =>
  (Array.isArray(files) ? files : [])
    .map(getInstallmentAttachmentPath)
    .filter(Boolean)
    .map(buildInstallmentAttachmentUrl)
    .filter(Boolean);

const toUtcDateTimeString = (dateValue) => {
  const normalized = String(dateValue || "").trim();
  if (!normalized) return "";
  return `${normalized}T00:00:00Z`;
};

const createInstallmentFormState = () => ({
  paymentMethod: "",
  amount: "",
  submissionDate: "",
  notes: "",
  files: [],
  bankName: "",
  accountTitle: "",
  ibanNumber: "",
  referenceNumber: "",
});

const normalizeVehicleStatusValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const hasImeiTrackerValue = (value) => String(value || "").trim() !== "";

const hasMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const mergeNonEmptyValues = (baseValues = {}, nextValues = {}) => {
  const merged = { ...(baseValues || {}) };

  Object.entries(nextValues || {}).forEach(([key, value]) => {
    if (hasMeaningfulValue(value)) {
      merged[key] = value;
    }
  });

  return merged;
};

const statusMeta = (status) => {
  const normalized = normalizeVehicleStatusValue(status);
  if (normalized === "active") {
    return { label: "Active", color: "#299900", icon: activeLoadStatusIcon };
  }
  if (normalized === "under_maintenance") {
    return {
      label: "Under Maintenance",
      color: "#f2841b",
      icon: statusUnderMaintenanceIcon,
    };
  }
  if (normalized === "decommissioned") {
    return {
      label: "Decommissioned",
      color: "#0a53e8",
      icon: statusDecommissionedIcon,
    };
  }
  return { label: "Inactive", color: "#ef0000", icon: inactiveStatusIcon };
};

const serviceStatusMeta = (status) => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "open") return { label: "Open", icon: openIcon, color: "#999999" };
  if (normalized === "pending") return { label: "Pending", icon: pendingIcon, color: "#ff7f02" };
  if (normalized === "closed") return { label: "Closed", icon: closedIcon, color: "#ef0000" };
  if (normalized === "in_progress") return { label: "In progress", icon: inProgressIcon, color: "#4014b8" };
  return { label: "Completed", icon: checkedIcon, color: "#299900" };
};

const tyreConfigurationOptions = [
  { label: "Single Tyres", value: "single" },
  { label: "Dual Tyres", value: "dual" },
];

const defaultAxleAssignments = {
  left: "",
  right: "",
  leftInner: "",
  leftOuter: "",
  rightInner: "",
  rightOuter: "",
};

const axleAssignmentFields = {
  single: [
    { key: "left", label: (axleId) => `Assign Tyre to ${axleId} Left` },
    { key: "right", label: (axleId) => `Assign Tyre to ${axleId} Right` },
  ],
  dual: [
    { key: "leftInner", label: (axleId) => `Assign Tyre to ${axleId} Left Inner` },
    { key: "leftOuter", label: (axleId) => `Assign Tyre to ${axleId} Left Outer` },
    { key: "rightInner", label: (axleId) => `Assign Tyre to ${axleId} Right Inner` },
    { key: "rightOuter", label: (axleId) => `Assign Tyre to ${axleId} Right Outer` },
  ],
};

const renderFieldLabel = (label, required = true) => (
  <span className="assets_vehicle_field_label">
    <span>{label}</span>
    {required ? <span className="assets_vehicle_field_required">*</span> : null}
  </span>
);

const findOption = (options = [], value = "") => {
  const normalizedValue = String(value || "").trim().toLowerCase();
  if (!normalizedValue) return null;

  return (
    options.find(
      (option) =>
        String(option?.value || "").toLowerCase() === normalizedValue ||
        String(option?.label || "").toLowerCase() === normalizedValue ||
        String(option?.optionId || option?.id || "").toLowerCase() === normalizedValue
    ) || null
  );
};

const ensureCurrentOption = (options = [], value = "") => {
  const normalizedValue = normalizeLinkedValue(value);
  if (!normalizedValue) return options;
  if (findOption(options, normalizedValue)) return options;

  return [{ label: normalizedValue, value: normalizedValue }, ...options];
};

const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : "";
  }

  return String(value).trim();
};

const normalizeComparableFiles = (files = []) =>
  normalizeUploadedFiles(files)
    .map((file) => String(file || "").trim())
    .filter(Boolean)
    .sort();

const areComparableArraysEqual = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const toLabelValueOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => ({
    ...option,
    optionId: option?.value ?? option?.id ?? option?.optionId,
    label: String(option?.label || option?.name || option?.title || option?.value || "").trim(),
    value: String(option?.label || option?.name || option?.title || option?.value || "").trim(),
  }));

const toIdValueOptions = (options = []) =>
  (Array.isArray(options) ? options : []).map((option) => {
    const label = String(
      option?.label || option?.name || option?.title || option?.company_name || option?.transporter_name || option?.value || ""
    ).trim();
    const rawValue = option?.value ?? option?.id ?? option?.vehicle_type_id ?? option?.optionId ?? label;

    return {
      ...option,
      optionId: getPositiveNumber(rawValue),
      label,
      value: rawValue,
    };
  }).filter((option) => option.label);

const ensureCurrentIdOption = (options = [], value = "", label = "") => {
  const normalizedValue = normalizeComparableValue(value);
  if (!normalizedValue) return options;
  if (findOption(options, normalizedValue)) return options;

  const normalizedLabel = normalizeComparableValue(label) || normalizedValue;
  return [
    {
      label: normalizedLabel,
      value,
      optionId: getPositiveNumber(value),
    },
    ...options,
  ];
};

const normalizeOwnershipType = (...values) => {
  for (const value of values) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, "_");

    if (normalized) return normalized;
  }

  return "";
};

const isHiredOwnershipType = (...values) =>
  normalizeOwnershipType(...values) === "hired";

const normalizeLinkedValue = (value) => {
  const normalized = String(value || "").trim();
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  if (lower === "0" || lower === "n/a" || lower === "null") return "";

  return normalized;
};

const normalizeSwapAssetType = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "equipment" ? "equipment" : "vehicle";
};

const getSwapAssetFieldLabel = (assetType = "") => {
  if (assetType === "equipment") return "Equipment ID";
  if (assetType === "vehicle") return "Vehicle ID";
  return "Asset ID";
};

const resolveSelectOptionId = (
  options = [],
  value = "",
  { fallbackId = null, initialValue = "" } = {}
) => {
  const matchedOption = findOption(options, value);
  const matchedId = getPositiveNumber(
    matchedOption?.optionId,
    matchedOption?.id,
    matchedOption?.value
  );

  if (matchedId) {
    return matchedId;
  }

  const normalizedValue = normalizeComparableValue(value).toLowerCase();
  const normalizedInitialValue = normalizeComparableValue(initialValue).toLowerCase();
  if (normalizedValue && normalizedValue === normalizedInitialValue) {
    return getPositiveNumber(fallbackId);
  }

  return null;
};

const buildVehicleTyreLinkSelections = (axleConfigurations = [], tyreOptions = []) =>
  (Array.isArray(axleConfigurations) ? axleConfigurations : []).map((axle, index) => {
    const axleId = String(axle?.axleId || `A${index + 1}`);
    const assignmentFields = getAxleAssignmentFields(axle?.tyreConfiguration);

    return {
      axleId,
      axleRecordId: getPositiveNumber(axle?.id),
      selections: assignmentFields.map((field) => {
        const value = axle?.assignments?.[field.key] || "";

        return {
          fieldKey: field.key,
          value,
          resolvedTyreId: resolveSelectOptionId(tyreOptions, value, {
            fallbackId: getPositiveNumber(value),
            initialValue: value,
          }),
        };
      }),
    };
  });

const linkedVehicleTyresSchema = Yup.array()
  .of(
    Yup.object().shape({
      axleId: Yup.string().required(),
      axleRecordId: Yup.number().typeError("Axle is required").required("Axle is required"),
      selections: Yup.array()
        .of(
          Yup.object().shape({
            fieldKey: Yup.string().required(),
            resolvedTyreId: Yup.number()
              .typeError("Tyre is required")
              .required("Tyre is required"),
          })
        )
        .min(1, "Tyre positions are required")
        .test(
          "all-positions-selected",
          "Please select tyres for all axle positions",
          (selections = []) =>
            selections.every((selection) => Boolean(getPositiveNumber(selection?.resolvedTyreId)))
        ),
    })
  )
  .min(1, "Vehicle axle data is missing");

const buildVehicleGeneralInfoSnapshot = ({
  vehicleId,
  values,
  attachments = [],
  makeId,
  modelId,
  transmissionTypeId,
  primaryDriverId,
  secondaryDriverId,
  helperId,
  branchId,
}) => ({
  vehicle_id: getPositiveNumber(vehicleId),
  registration_number: normalizeComparableValue(values?.registrationNumber),
  chassis_number: normalizeComparableValue(values?.vinChassisNo),
  make_id: getPositiveNumber(makeId),
  model_id: getPositiveNumber(modelId),
  manufacturing_year: normalizeNumber(values?.year, 0),
  fuel_type: String(values?.fuelType || "").trim().toLowerCase(),
  // fuel_tank_type: String(values?.fuelTankType || "").trim().toLowerCase(),
  tank_capacity: normalizeNumber(values?.tankCapacity, 0),
  initial_fuel_efficiency: normalizeNumber(values?.currentFuelEfficiency, 0),
  // odometer_unit: String(values?.odometerUnit || "").trim().toLowerCase(),
  transmission_type: getPositiveNumber(transmissionTypeId),
  engine_capacity: normalizeNumber(values?.engineCapacity, 0),
  imeino: normalizeComparableValue(values?.imeino) || null,
  primary_driver_id: getPositiveNumber(primaryDriverId),
  secondary_driver_id: getPositiveNumber(secondaryDriverId),
  helper_id: getPositiveNumber(helperId),
  branch_id: getPositiveNumber(branchId),
  status: normalizeVehicleStatusValue(values?.status),
  number_of_axles: normalizeNumber(values?.numberOfAxles, 0),
  description: String(values?.description || "").trim(),
  files: normalizeUploadedFiles(attachments),
  ...(String(values?.trackingPackage || "").trim()
    ? { tracking_package: String(values.trackingPackage || "").trim().toLowerCase() }
    : {}),
});

const getUpdatedVehicleGeneralInfoKeys = (currentSnapshot = {}, initialSnapshot = {}) =>
  Object.keys(currentSnapshot).filter((key) => {
    if (key === "vehicle_id") return false;

    if (key === "files") {
      return !areComparableArraysEqual(
        normalizeComparableFiles(currentSnapshot[key]),
        normalizeComparableFiles(initialSnapshot[key])
      );
    }

    return (
      normalizeComparableValue(currentSnapshot[key]) !==
      normalizeComparableValue(initialSnapshot[key])
    );
  });

const buildVehiclePurchaseInfoSnapshot = (vehicleId, values = {}) => ({
  vehicle_id: getPositiveNumber(vehicleId),
  purchase_at: normalizeComparableValue(values.purchaseDate),
  purchase_cost: normalizeNumber(values.purchaseCost, 0),
  payment_type: normalizeComparableValue(values.paymentType),
  depreciation_method: normalizeComparableValue(values.depreciationMethod),
  useful_life: normalizeNumber(values.usefulLife, 0),
  residual_value: normalizeNumber(values.residualValue, 0),
  asset_insurances: {
    provider: normalizeComparableValue(values.insuranceProvider),
    policy_number: normalizeComparableValue(values.policyNumber),
    coverage_type: normalizeComparableValue(values.coverageType),
    coverage_amount: normalizeNumber(values.coverageAmount, 0),
    premium_amount: normalizeNumber(values.premiumAmount, 0),
    start_date: normalizeComparableValue(values.insuranceStartDate),
    expiry_date: normalizeComparableValue(values.insuranceExpiryDate),
  },
});

const getUpdatedVehiclePurchaseInfoKeys = (currentSnapshot = {}, initialSnapshot = {}) => {
  const updatedKeys = [];

  Object.entries(currentSnapshot).forEach(([key, value]) => {
    if (key === "vehicle_id") return;

    if (key === "asset_insurances") {
      Object.entries(value || {}).forEach(([insuranceKey, insuranceValue]) => {
        if (
          normalizeComparableValue(insuranceValue) !==
          normalizeComparableValue(initialSnapshot?.asset_insurances?.[insuranceKey])
        ) {
          updatedKeys.push(`asset_insurances.${insuranceKey}`);
        }
      });
      return;
    }

    if (
      normalizeComparableValue(value) !==
      normalizeComparableValue(initialSnapshot[key])
    ) {
      updatedKeys.push(key);
    }
  });

  return updatedKeys;
};

const swapTypeOptions = [
  { label: "Vehicle", value: "vehicle" },
  { label: "Equipment", value: "equipment" },
];

const createAxleConfiguration = (index, savedConfiguration = {}) => ({
  axleId: savedConfiguration?.axleId || `A${index + 1}`,
  tyreConfiguration:
    savedConfiguration?.tyreConfiguration || (index === 1 ? "dual" : "single"),
  assignments: {
    ...defaultAxleAssignments,
    ...(savedConfiguration?.assignments || {}),
  },
});

const buildAxleConfigurations = (savedConfigurations = [], axleCount = 2) =>
  Array.from({ length: Math.max(1, axleCount) }, (_, index) =>
    createAxleConfiguration(index, savedConfigurations[index])
  );

const getAxleAssignmentFields = (tyreConfiguration) =>
  axleAssignmentFields[tyreConfiguration] || axleAssignmentFields.single;

const mapAssetTyreOptions = (assetTyres = []) =>
  (Array.isArray(assetTyres) ? assetTyres : []).map((item) => {
    const fallbackValue = [
      item?.axle_id,
      item?.position_identifier,
      item?.tyre_id || item?.tyre?.id,
    ]
      .filter(Boolean)
      .join(":");
    const tyreLabel =
      String(item?.tyre?.tyres_id || "").trim() ||
      (item?.tyre_id || item?.tyre?.id ? `TY-${item?.tyre_id || item?.tyre?.id}` : "Unassigned");

    return {
      label: `${item?.position || "Position"} - ${tyreLabel}`,
      value: String(item?.id || fallbackValue),
      optionId: getPositiveNumber(item?.id),
      assignmentId: getPositiveNumber(item?.id),
      axleId: getPositiveNumber(item?.axle_id),
      tyreId: getPositiveNumber(item?.tyre_id, item?.tyre?.id),
      position: String(item?.position || "").trim(),
      positionIdentifier: String(item?.position_identifier || "").trim(),
      tyreSystemId: String(item?.tyre?.tyres_id || "").trim(),
    };
  });

const createSwapTyreFormState = () => ({
  sourceType: "vehicle",
  sourceAssetId: "",
  sourcePosition: "",
  targetType: "",
  targetAssetId: "",
  targetPosition: "",
});

const getUserImage = (user) =>
  user?.image ||
  user?.profile_image ||
  user?.profileImage ||
  user?.avatar ||
  user?.details?.image ||
  user?.details?.avatar ||
  user?.user?.image ||
  "";

const isPlaceholderDisplayValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  return (
    !normalized ||
    normalized === "-" ||
    normalized === "--" ||
    normalized === "n/a" ||
    normalized === "null"
  );
};

const getPersonLabel = (...sources) => {
  for (const source of sources) {
    if (!source) continue;

    if (typeof source === "string" || typeof source === "number") {
      const normalized = String(source).trim();
      if (!isPlaceholderDisplayValue(normalized)) return normalized;
      continue;
    }

    const normalized = String(
      source?.username || source?.name || source?.label || source?.title || ""
    ).trim();
    if (!isPlaceholderDisplayValue(normalized)) return normalized;
  }

  return "";
};

const normalizePersonStatusValue = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const getPersonStatus = (...sources) => {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;

    const status =
      source?.status ||
      source?.user_status ||
      source?.driver_status ||
      source?.helper_status ||
      source?.user?.status;

    if (hasMeaningfulValue(status)) {
      return status;
    }
  }

  return "";
};

const isActivePersonStatus = (status) => normalizePersonStatusValue(status) === "active";

const buildPersonSelectOption = ({ id, label, image, status }) => {
  const normalizedLabel = String(label || "").trim();
  if (isPlaceholderDisplayValue(normalizedLabel)) return null;

  const optionId = getPositiveNumber(id);
  const normalizedStatus = normalizePersonStatusValue(status);

  return {
    value: optionId || normalizedLabel,
    optionId: optionId || undefined,
    label: normalizedLabel,
    image: image || "",
    showIcon: true,
    status: normalizedStatus || undefined,
    ...(normalizedStatus ? { isDisabled: !isActivePersonStatus(normalizedStatus) } : {}),
  };
};

const mapPersonDropdownOption = (item = {}) => {
  const status = getPersonStatus(item);

  return {
    value: item?.value,
    optionId: getPositiveNumber(item?.value),
    label: String(item?.label || "").trim(),
    image: item?.image,
    showIcon: true,
    status: normalizePersonStatusValue(status),
    isDisabled: !isActivePersonStatus(status),
  };
};

const mergeSelectOptions = (...groups) => {
  const merged = [];
  const seen = new Set();

  groups.flat().forEach((option) => {
    if (!option?.label) return;

    const optionId = normalizeComparableValue(
      option?.optionId ?? option?.id ?? option?.value
    );
    const identity = optionId
      ? `id:${String(optionId).toLowerCase()}`
      : `label:${String(option.label).trim().toLowerCase()}`;

    if (seen.has(identity)) return;
    seen.add(identity);
    merged.push(option);
  });

  return merged;
};

const getInitialValues = (vehicle, vehicleDetailData = null) => {
  const formData = vehicle?.formData || {};
  const vehicleFiles =
    extractVehicleFiles(vehicleDetailData?.files).length > 0
      ? extractVehicleFiles(vehicleDetailData?.files)
      : extractVehicleFiles(vehicleDetailData).length > 0
      ? extractVehicleFiles(vehicleDetailData)
      : extractVehicleFiles(vehicle?.files).length > 0
      ? extractVehicleFiles(vehicle?.files)
      : extractVehicleFiles(formData).length > 0
      ? extractVehicleFiles(formData)
      : extractVehicleFiles(vehicle);
  const attachments = Array.isArray(vehicleFiles) ? vehicleFiles : [];
  const numberOfAxles = String(formData?.numberOfAxles || 2);
  const hiredVehicle = isHiredOwnershipType(
    vehicleDetailData?.ownership_type,
    vehicleDetailData?.ownershipType,
    vehicle?.ownership_type,
    vehicle?.ownershipTypeRaw,
    formData?.ownershipTypeRaw,
    vehicle?.ownershipType,
    formData?.ownershipType
  );
  const threePlVehicleTypeId =
    formData?.vehicleTypeId ||
    vehicleDetailData?.vehicle_type_id ||
    vehicleDetailData?.vehicle_type?.id ||
    vehicleDetailData?.type?.id ||
    vehicle?.vehicleTypeId ||
    "";
  const primaryDriverId = getPositiveNumber(
    formData?.primaryDriverId,
    formData?.assignedDriverId,
    vehicleDetailData?.primary_driver_id,
    vehicleDetailData?.primary_driver?.id,
    vehicleDetailData?.driver_id,
    vehicleDetailData?.driver?.id,
    vehicleDetailData?.assigned_driver?.id,
    vehicle?.primaryDriver?.id,
    vehicle?.assignedDriver?.id
  );
  const secondaryDriverId = getPositiveNumber(
    formData?.secondaryDriverId,
    vehicleDetailData?.secondary_driver_id,
    vehicleDetailData?.secondary_driver?.id,
    vehicle?.secondaryDriver?.id
  );
  const helperId = getPositiveNumber(
    formData?.helperId,
    vehicleDetailData?.helper_id,
    vehicleDetailData?.helper?.id,
    vehicle?.helper?.id
  );
  const primaryDriverLabel = getPersonLabel(
    formData?.primaryDriver,
    formData?.assignedDriver,
    vehicleDetailData?.primary_driver,
    vehicleDetailData?.driver,
    vehicleDetailData?.assigned_driver,
    vehicle?.primaryDriver,
    vehicle?.assignedDriver
  );
  const secondaryDriverLabel = getPersonLabel(
    formData?.secondaryDriver,
    vehicleDetailData?.secondary_driver,
    vehicle?.secondaryDriver
  );
  const helperLabel = getPersonLabel(
    formData?.helper,
    vehicleDetailData?.helper,
    vehicle?.helper
  );

  return {
    systemId: vehicle?.systemId || "",
    transporter:
      formData?.transporter ||
      formData?.transporterId ||
      vehicleDetailData?.transporter_id ||
      vehicleDetailData?.transporter?.id ||
      vehicleDetailData?.three_pl_transporter_id ||
      vehicleDetailData?.threePlTransporter?.id ||
      vehicleDetailData?.vehicle_3pl?.transporter_id ||
      vehicleDetailData?.vehicle_hired?.transporter_id ||
      vehicle?.transporter ||
      "",
    transporterName:
      formData?.transporterName ||
      vehicleDetailData?.transporter?.name ||
      vehicleDetailData?.transporter?.label ||
      vehicleDetailData?.transporter_name ||
      vehicleDetailData?.threePlTransporter?.name ||
      vehicleDetailData?.vehicle_3pl?.transporter?.name ||
      vehicleDetailData?.vehicle_hired?.transporter?.name ||
      vehicle?.transporterName ||
      "",
    registrationNumber: formData?.registrationNumber || vehicle?.registrationNumber || "",
    vinChassisNo: formData?.vinChassisNo || vehicle?.vinChassisNo || "",
    make: formData?.make || vehicle?.make || "",
    model: formData?.model || vehicle?.model || "",
    vehicleType: hiredVehicle ? threePlVehicleTypeId : formData?.vehicleType || vehicle?.vehicleType || "",
    vehicleTypeId: threePlVehicleTypeId,
    loadCapacity:
      formData?.loadCapacity ||
      vehicleDetailData?.load_capacity ||
      vehicleDetailData?.loadCapacity ||
      vehicleDetailData?.vehicle_3pl?.load_capacity ||
      vehicleDetailData?.vehicle_hired?.load_capacity ||
      vehicle?.loadCapacity ||
      "",
    driverId:
      vehicleDetailData?.hired_vehicle_driver?.id ||
      vehicle?.hired_vehicle_driver?.id ||
      formData?.driverId ||
      "",
    driverName:
      vehicleDetailData?.hired_vehicle_driver?.name ||
      vehicle?.hired_vehicle_driver?.name ||
      formData?.driverName ||
      vehicleDetailData?.driver_name ||
      vehicleDetailData?.driverName ||
      vehicleDetailData?.vehicle_3pl?.driver_name ||
      vehicleDetailData?.vehicle_hired?.driver_name ||
      vehicle?.driverName ||
      "",
    mobileNumber:
      vehicleDetailData?.hired_vehicle_driver?.mobile_no ||
      vehicle?.hired_vehicle_driver?.mobile_no ||
      formData?.mobileNumber ||
      vehicleDetailData?.mobile_no ||
      vehicleDetailData?.mobileNo ||
      vehicleDetailData?.contact ||
      vehicleDetailData?.vehicle_3pl?.mobile_no ||
      vehicleDetailData?.vehicle_hired?.mobile_no ||
      vehicle?.mobileNumber ||
      "",
    cnic:
      vehicleDetailData?.hired_vehicle_driver?.cnic ||
      vehicle?.hired_vehicle_driver?.cnic ||
      formData?.cnic ||
      vehicleDetailData?.cnic ||
      vehicleDetailData?.vehicle_3pl?.cnic ||
      vehicleDetailData?.vehicle_hired?.cnic ||
      vehicle?.cnic ||
      "",
    year: formData?.year || vehicle?.year || "",
    fuelType: formData?.fuelType || "diesel",
    fuelTankType: formData?.fuelTankType || "single_tank",
    tankCapacity: formData?.tankCapacity || "",
    currentFuelEfficiency: formData?.currentFuelEfficiency || "",
    odometerUnit: formData?.odometerUnit || "km",
    transmissionType: formData?.transmissionType || "",
    engineCapacity: formData?.engineCapacity || "",
    imeino: formData?.imeino || vehicle?.imeino || "",
    trackingPackage:
      formData?.trackingPackage ||
      formData?.tracking_package ||
      vehicle?.trackingPackage ||
      vehicle?.tracking_package ||
      "",
    primaryDriver: primaryDriverId || primaryDriverLabel || "",
    secondaryDriver: secondaryDriverId || secondaryDriverLabel || "",
    helper: helperId || helperLabel || "",
    branch: formData?.branch || vehicle?.branch || "",
    status: normalizeVehicleStatusValue(formData?.status || vehicle?.status || "active"),
    linkedEquipmentId:
      normalizeLinkedValue(formData?.linkedEquipmentId) ||
      normalizeLinkedValue(vehicle?.linkedEquipmentId),
    numberOfAxles,
    axleConfigurations: buildAxleConfigurations(
      Array.isArray(formData?.axleConfigurations) ? formData.axleConfigurations : [],
      normalizeNumber(numberOfAxles, 2)
    ),
    description: formData?.description || "",
    purchaseDate: formData?.purchaseDate || "",
    purchaseCost: formData?.purchaseCost || vehicle?.totalCost || "",
    paymentType: formData?.paymentType || "",
    depreciationMethod: formData?.depreciationMethod || "",
    usefulLife: formData?.usefulLife || "",
    residualValue: formData?.residualValue || "",
    policyNumber:
      formData?.policyNumber ||
      formData?.policy_number ||
      vehicleDetailData?.policy_number ||
      vehicleDetailData?.insurance?.policy_number ||
      vehicleDetailData?.insurance?.policyNumber ||
      "",
    insuranceProvider:
      formData?.insuranceProvider ||
      formData?.insurance_provider ||
      vehicleDetailData?.insurance_provider ||
      vehicleDetailData?.insurance?.insurance_provider ||
      vehicleDetailData?.insurance?.provider ||
      "",
    coverageType:
      formData?.coverageType ||
      formData?.coverage_type ||
      vehicleDetailData?.coverage_type ||
      vehicleDetailData?.insurance?.coverage_type ||
      vehicleDetailData?.insurance?.coverageType ||
      "",
    coverageAmount:
      formData?.coverageAmount ||
      formData?.coverage_amount ||
      vehicleDetailData?.coverage_amount ||
      vehicleDetailData?.insurance?.coverage_amount ||
      vehicleDetailData?.insurance?.coverageAmount ||
      "",
    premiumAmount:
      formData?.premiumAmount ||
      formData?.premium_amount ||
      vehicleDetailData?.premium_amount ||
      vehicleDetailData?.insurance?.premium_amount ||
      vehicleDetailData?.insurance?.premiumAmount ||
      "",
    insuranceStartDate: formatDateOnly(
      formData?.insuranceStartDate ||
        formData?.insurance_start_at ||
        vehicleDetailData?.insurance_start_at ||
        vehicleDetailData?.insurance?.insurance_start_at ||
        vehicleDetailData?.insurance?.start_date ||
        vehicleDetailData?.insurance?.startDate ||
        ""
    ),
    insuranceExpiryDate: formatDateOnly(
      formData?.insuranceExpiryDate ||
        formData?.insurance_expire_at ||
        vehicleDetailData?.insurance_expire_at ||
        vehicleDetailData?.insurance?.insurance_expire_at ||
        vehicleDetailData?.insurance?.expiry_date ||
        vehicleDetailData?.insurance?.expiryDate ||
        ""
    ),
    insuranceStatus:
      formData?.insuranceStatus ||
      formData?.insurance_status ||
      vehicleDetailData?.insurance_status ||
      vehicleDetailData?.insurance?.status ||
      "",
    insuranceCost:
      formData?.insuranceCost ||
      formData?.insurance_cost ||
      formData?.premiumAmount ||
      formData?.premium_amount ||
      vehicleDetailData?.premium_amount ||
      vehicleDetailData?.insurance?.premium_amount ||
      "",
    maintenanceCost: formData?.maintenanceCost || "",
    notes: formData?.notes || vehicle?.notes || "",
    attachments,
    files: attachments,
    driverDocument: attachments,
  };
};

const detailsSchema = Yup.object().shape({
  registrationNumber: Yup.string().required("Registration No. is required"),
  vinChassisNo: Yup.string().required("VIN / Chassis No. is required"),
  make: Yup.string().required("Make is required"),
  model: Yup.string().required("Model is required"),
  year: Yup.number().typeError("Year must be numeric").required("Year is required"),
  fuelType: Yup.string().required("Fuel Type is required"),
  // fuelTankType: Yup.string().required("Fuel Tank Type is required"),
  // odometerUnit: Yup.string().required("Odometer Unit is required"),
  transmissionType: Yup.string().required("Transmission Type is required"),
  engineCapacity: Yup.number().typeError("Engine Capacity must be numeric").positive("Engine Capacity must be greater than zero").required("Engine Capacity is required"),
  primaryDriver: Yup.mixed().required("Primary Driver is required"),
  branch: Yup.string().required("Branch is required"),
  status: Yup.string().required("Status is required"),
  numberOfAxles: Yup.number()
    .typeError("Number of Axles must be numeric")
    .integer("Number of Axles must be a whole number")
    .positive("Number of Axles must be greater than zero")
    .required("Number of Axles is required"),
  trackingPackage: Yup.string().when("imeino", {
    is: (value) => hasImeiTrackerValue(value),
    then: (schema) =>
      schema.required("Tracking Package is required when IMEI Tracker Code is provided"),
    otherwise: (schema) => schema.notRequired(),
  }),
  description: Yup.string().test("desc-max", "Description too long", (value) => stripHtml(value).length <= 1000).nullable(),
});

const threePlVehicleDetailsSchema = Yup.object().shape({
  transporter: Yup.string().required("Transporter / Vendor is required"),
  registrationNumber: Yup.string().trim().required("Registration No. is required"),
  vehicleType: Yup.string().required("Vehicle Type is required"),
  driverName: Yup.string().trim().required("Driver Name is required"),
  mobileNumber: buildPhoneNumberSchema("Mobile No. is required"),
  cnic: buildCnicSchema("CNIC is required"),
  attachments: Yup.array()
    .test(
      "has-documents",
      "Driver CNIC / License is required",
      (value) => Array.isArray(value) && value.length > 0
    )
    .required("Driver CNIC / License is required"),
});

const buildThreePlVehiclePayload = (values = {}, vehicleId = null) => ({
  id: Number(vehicleId || values.id) || undefined,
  vehicle_id: Number(vehicleId || values.id) || undefined,
  transporter_id: Number(values.transporter),
  registration_number: String(values.registrationNumber || "").trim(),
  vehicle_type_id: Number(values.vehicleType),
  driver_name: String(values.driverName || "").trim(),
  mobile_no: String(values.mobileNumber || "").trim(),
  cnic: String(values.cnic || "").trim(),
  files: normalizeUploadedFiles(values.attachments || values.files || values.driverDocument || []),
});

const financialInsuranceSchema = Yup.object().shape({
  purchaseDate: Yup.string().required("Purchase Date is required"),
  purchaseCost: Yup.number()
    .typeError("Purchase Cost must be numeric")
    .min(0, "Purchase Cost cannot be negative")
    .required("Purchase Cost is required"),
  paymentType: Yup.string().required("Payment Type is required"),
  depreciationMethod: Yup.string().required("Depreciation Method is required"),
  usefulLife: Yup.number()
    .typeError("Useful Life must be numeric")
    .positive("Useful Life must be greater than zero")
    .required("Useful Life is required"),
  residualValue: Yup.number()
    .typeError("Residual Value must be numeric")
    .min(0, "Residual Value cannot be negative")
    .required("Residual Value is required"),
  policyNumber: Yup.string().required("Policy Number is required"),
  insuranceProvider: Yup.string().required("Provider is required"),
  coverageType: Yup.string().required("Coverage Type is required"),
  coverageAmount: Yup.number()
    .typeError("Coverage Amount must be numeric")
    .min(0, "Coverage Amount cannot be negative")
    .required("Coverage Amount is required"),
  premiumAmount: Yup.number()
    .typeError("Premium must be numeric")
    .min(0, "Premium cannot be negative")
    .required("Premium is required"),
  insuranceStartDate: Yup.string().required("Start Date is required"),
  insuranceExpiryDate: Yup.string().required("Expiry Date is required"),
});

const tabOptions = [
  { key: "general_info", label: "General Information" },
  { key: "service_records", label: "Service Records" },
  { key: "assignment_history", label: "Assignment History" },
  { key: "financial_insurance", label: "Financial & Insurance" },
  { key: "operational_data", label: "Operational Data" },
];

const VehicleDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canUpdate = hasPermission({
    permissionSlug: AssetsPermission.UpdateVehicle,
    menuSlug: AssetsSubModules.Assets,
    moduleSlug: AppMainModules.assetsManagement,
  });
  const location = useLocation();
  const { systemId } = useParams();
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "vehicles")
  );
  const {
    vehicleMakesDropdownData,
    vehicleModelsDropdownData,
    vehicleTransmissionTypesDropdownData,
    vehicleCompanyBranchesDropdownData,
  } = useSelector((state) => state.vehicleDropdowns);
  const {
    vehiclesDropdownData = [],
    banksDropdownData = [],
    threePlTransporterDropdownData = [],
    transporterVehicleTypesDropdownData = [],
    loading: dropdownLoading = false,
  } = useSelector((state) => state.dropdowns || {});
  const {
    equipmentDropdownData = [],
    dropdownLoadingCount = 0,
  } = useSelector((state) => state.equipment || {});
  const {
    tyresDropdownData = null,
    dropdownLoadingCount: tyreDropdownLoadingCount = 0,
    assetTyresData = [],
    assetTyresLoading = false,
    assetTyresRequest = null,
  } = useSelector((state) => state.tyre || {});
  const {
    vehicleSingleDetailData,
    vehicleSingleDetailError = null,
    vehicleHistoryData = [],
    vehicleHistoryLoading = false,
    vehicleTripsData = [],
    vehicleTripsLoading = false,
    vehicleTripsMeta = {},
    vehicleFuelLogsData = [],
    vehicleFuelLogsLoading = false,
    vehicleFuelLogsMeta = {},
    vehicleDistanceSummaryData = null,
    vehicleDistanceSummaryLoading = false,
    vehicleInstallmentsData = [],
    vehicleInstallmentsLoading = false,
    vehicleAssignmentHistoryData = null,
    vehicleAssignmentHistoryLoading = false,
    attachVehicleEquipmentLoading = false,
    attachVehicleTyresLoading = false,
    detachVehicleTyreLoading = false,
    createVehicleInstallmentLoading = false,
    updateVehiclePurchaseInfoLoading = false,
    vehicleServiceRecordsData = [],
    vehicleServiceRecordsLoading = false,
    vehicleServiceRecordsMeta = {},
  } = useSelector((state) => state.services);
  const { getFuelStationsData = [] } = useSelector((state) => state.fuel || {});

  const [activeTab, setActiveTab] = useState("general_info");
  const [commentInput, setCommentInput] = useState("");
  const [comments, setComments] = useState([]);
  const [activities, setActivities] = useState([]);
  const [showSwapTyresModal, setShowSwapTyresModal] = useState(false);
  const [showAddInstallmentModal, setShowAddInstallmentModal] = useState(false);
  const [attachmentViewer, setAttachmentViewer] = useState({
    open: false,
    urls: [],
    currentIndex: 0,
  });
  const [swapTyreForm, setSwapTyreForm] = useState(createSwapTyreFormState);
  const [swapTyresLoading, setSwapTyresLoading] = useState(false);
  const [detachingTyreFieldKey, setDetachingTyreFieldKey] = useState("");
  const [targetAssetTyreOptions, setTargetAssetTyreOptions] = useState([]);
  const [targetAssetTyresLoading, setTargetAssetTyresLoading] = useState(false);
  const [installmentForm, setInstallmentForm] = useState(createInstallmentFormState);
  const [installmentData, setInstallmentData] = useState([]);
  const [linkedEquipmentDraft, setLinkedEquipmentDraft] = useState("");
  const [isEquipmentSelectionUnlocked, setIsEquipmentSelectionUnlocked] = useState(true);
  const [driverDropdownOptions, setDriverDropdownOptions] = useState([]);
  const [driverDropdownLoading, setDriverDropdownLoading] = useState(false);
  const [helperDropdownOptions, setHelperDropdownOptions] = useState([]);
  const [helperDropdownLoading, setHelperDropdownLoading] = useState(false);

  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentLimit, setEquipmentLimit] = useState(10);
  const [servicePage, setServicePage] = useState(1);
  const [serviceLimit, setServiceLimit] = useState(10);
  const [installmentPage, setInstallmentPage] = useState(1);
  const [installmentLimit, setInstallmentLimit] = useState(10);
  const [tripPage, setTripPage] = useState(1);
  const [tripLimit, setTripLimit] = useState(10);
  const [fuelPage, setFuelPage] = useState(1);
  const [fuelLimit, setFuelLimit] = useState(10);
  const [tyrePage, setTyrePage] = useState(1);
  const [tyreLimit, setTyreLimit] = useState(10);

  const currentUser = useMemo(() => getCurrentUser(), []);

  const openAttachmentViewer = (files = []) => {
    const urls = resolveInstallmentAttachmentUrls(files);

    if (!urls.length) {
      errorNotify("No attachments found");
      return;
    }

    setAttachmentViewer({
      open: true,
      urls,
      currentIndex: 0,
    });
  };

  const closeAttachmentViewer = () => {
    setAttachmentViewer({
      open: false,
      urls: [],
      currentIndex: 0,
    });
  };

  const changeAttachmentSlide = (direction) => {
    setAttachmentViewer((previous) => {
      const total = previous.urls.length;
      if (!total) return previous;

      return {
        ...previous,
        currentIndex: (previous.currentIndex + direction + total) % total,
      };
    });
  };

  useEffect(() => {
    dispatch(fetchVehicleMakesDropdown());
    dispatch(fetchVehicleModelsDropdown());
    dispatch(fetchVehicleTransmissionTypesDropdown());
    dispatch(fetchVehicleCompanyBranchesDropdown());
    dispatch(fetchEquipmentDropdown());
    dispatch(fetchTyresDropdown());
    dispatch(getFuelStations());
    dispatch(fetchBanksDropdown());
    dispatch(fetchVehiclesDropdown());
    dispatch(fetchThreePlTransporterDropdown());
    dispatch(fetchTransporterVehicleTypesDropdown());

    return () => {
      dispatch(clearVehicleDropdownState());
    };
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const loadDriverDropdown = async () => {
      setDriverDropdownLoading(true);

      try {
        const response = await apiService.getDriverDropdown();
        if (!isMounted) return;

        const source = Array.isArray(response?.data) ? response.data : [];
        setDriverDropdownOptions(
          source
            .map(mapPersonDropdownOption)
            .filter((item) => item.label)
        );
      } catch {
        if (isMounted) {
          setDriverDropdownOptions([]);
        }
      } finally {
        if (isMounted) {
          setDriverDropdownLoading(false);
        }
      }
    };

    loadDriverDropdown();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadHelperDropdown = async () => {
      setHelperDropdownLoading(true);

      try {
        const response = await apiService.getHelperDropdown();
        if (!isMounted) return;

        const source = Array.isArray(response?.data) ? response.data : [];
        setHelperDropdownOptions(
          source
            .map(mapPersonDropdownOption)
            .filter((item) => item.label)
        );
      } catch {
        if (isMounted) {
          setHelperDropdownOptions([]);
        }
      } finally {
        if (isMounted) {
          setHelperDropdownLoading(false);
        }
      }
    };

    loadHelperDropdown();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearVehicleHistoryState());
      dispatch(clearVehicleServiceRecordsState());
      dispatch(clearVehicleOperationalDataState());
      dispatch(clearVehicleInstallmentsState());
    };
  }, [dispatch]);

  const allVehicles = useMemo(() => [], []);
  const allEquipments = useMemo(() => [], []);
  const allTyres = useMemo(() => [], []);

  const makeApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleMakesDropdownData)
      ? vehicleMakesDropdownData
      : findCollectionByKeys(vehicleMakesDropdownData, [
        "vehiclemakes",
        "makes",
        "vehiclemakesdropdown",
      ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "make_id"],
        labelKeys: ["label", "make_name", "name", "title"],
        defaultLabelPrefix: "Make",
      })
    );
  }, [vehicleMakesDropdownData]);

  const modelApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleModelsDropdownData)
      ? vehicleModelsDropdownData
      : findCollectionByKeys(vehicleModelsDropdownData, [
        "vehiclemodels",
        "models",
        "vehiclemodelsdropdown",
      ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "model_id"],
        labelKeys: ["label", "model_name", "name", "title"],
        defaultLabelPrefix: "Model",
      })
    );
  }, [vehicleModelsDropdownData]);

  const transmissionApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleTransmissionTypesDropdownData)
      ? vehicleTransmissionTypesDropdownData
      : findCollectionByKeys(vehicleTransmissionTypesDropdownData, [
        "transmissiontypes",
        "vehicletransmissiontypes",
        "transmissiontypedropdown",
      ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "transmission_type_id"],
        labelKeys: ["label", "transmission_type_name", "name", "title"],
        defaultLabelPrefix: "Transmission Type",
      })
    );
  }, [vehicleTransmissionTypesDropdownData]);

  const branchApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleCompanyBranchesDropdownData)
      ? vehicleCompanyBranchesDropdownData
      : findCollectionByKeys(vehicleCompanyBranchesDropdownData, [
        "companybranches",
        "branches",
        "branchdropdown",
      ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "branch_id"],
        labelKeys: ["label", "branch_name", "name", "title"],
        defaultLabelPrefix: "Branch",
      })
    );
  }, [vehicleCompanyBranchesDropdownData]);

  const threePlTransporterOptions = useMemo(() => {
    const source = Array.isArray(threePlTransporterDropdownData)
      ? threePlTransporterDropdownData
      : findCollectionByKeys(threePlTransporterDropdownData, [
        "transporters",
        "threepltransporters",
        "three_pl_transporters",
      ]);

    return toIdValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "transporter_id"],
        labelKeys: ["label", "name", "title", "transporter_name", "company_name"],
        defaultLabelPrefix: "Transporter",
      })
    );
  }, [threePlTransporterDropdownData]);

  const threePlVehicleTypeOptions = useMemo(() => {
    const source = Array.isArray(transporterVehicleTypesDropdownData)
      ? transporterVehicleTypesDropdownData
      : findCollectionByKeys(transporterVehicleTypesDropdownData, [
        "vehicletypes",
        "vehicle_types",
        "transportervehicletypes",
      ]);

    const apiOptions = toIdValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "vehicle_type_id"],
        labelKeys: ["label", "vehicle_type_name", "name", "title"],
        defaultLabelPrefix: "Vehicle Type",
      })
    );

    return apiOptions.length ? apiOptions : toIdValueOptions(vehicleTypeOptions);
  }, [transporterVehicleTypesDropdownData]);

  const fuelStationOptions = useMemo(() => {
    const source = Array.isArray(getFuelStationsData)
      ? getFuelStationsData
      : findCollectionByKeys(getFuelStationsData, [
        "fuelstations",
        "stations",
        "fuelstationdropdown",
      ]);

    return toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "fuel_station_id"],
        labelKeys: ["label", "fuel_station_name", "name", "title"],
        defaultLabelPrefix: "Fuel Station",
      })
    );
  }, [getFuelStationsData]);

  const decodedSystemId = useMemo(
    () => decodeURIComponent(systemId || location?.state?.vehicle?.systemId || ""),
    [location?.state?.vehicle?.systemId, systemId]
  );

  const detailVehicleId = useMemo(
    () => resolveVehicleDetailId(systemId, location?.state?.vehicle),
    [location?.state?.vehicle, systemId]
  );

  const localVehicle = useMemo(() => {
    if (!decodedSystemId) return location?.state?.vehicle || null;

    return (
      allVehicles.find(
        (row) =>
          String(row?.systemId || "").toLowerCase() === decodedSystemId.toLowerCase()
      ) || location?.state?.vehicle || null
    );
  }, [allVehicles, decodedSystemId, location?.state?.vehicle]);

  const mappedVehicleDetail = useMemo(() => {
    if (!detailVehicleId || !vehicleSingleDetailData) return null;

    const normalizedDetailId = parseInt(vehicleSingleDetailData?.id, 10);
    if (
      Number.isFinite(normalizedDetailId) &&
      normalizedDetailId > 0 &&
      normalizedDetailId !== detailVehicleId
    ) {
      return null;
    }

    return mapVehicleDetailRecord(vehicleSingleDetailData, {
      fallbackVehicle: localVehicle,
      makeOptions: makeApiOptions,
      modelOptions: modelApiOptions,
      transmissionOptions: transmissionApiOptions,
      branchOptions: branchApiOptions,
    });
  }, [
    branchApiOptions,
    detailVehicleId,
    localVehicle,
    makeApiOptions,
    modelApiOptions,
    transmissionApiOptions,
    vehicleSingleDetailData,
  ]);

  const vehicle = useMemo(() => {
    if (mappedVehicleDetail && localVehicle?.isCustom) {
      return {
        ...mappedVehicleDetail,
        ...localVehicle,
        formData: {
          ...(mappedVehicleDetail.formData || {}),
          ...(localVehicle.formData || {}),
        },
      };
    }

    return mappedVehicleDetail || localVehicle || null;
  }, [localVehicle, mappedVehicleDetail]);

  const isHiredVehicle = useMemo(
    () =>
      isHiredOwnershipType(
        vehicleSingleDetailData?.ownership_type,
        vehicleSingleDetailData?.ownershipType,
        vehicle?.ownership_type,
        vehicle?.ownershipTypeRaw,
        vehicle?.formData?.ownershipTypeRaw,
        vehicle?.ownershipType,
        vehicle?.formData?.ownershipType
      ),
    [vehicle, vehicleSingleDetailData]
  );

  const detailTabOptions = useMemo(
    () => (isHiredVehicle ? tabOptions.filter((tab) => tab.key === "general_info") : tabOptions),
    [isHiredVehicle]
  );

  const hasVehicleDetailSource = Boolean(detailVehicleId);

  const vehicleApiId = useMemo(() => {
    const candidates = [
      vehicleSingleDetailData?.id,
      mappedVehicleDetail?.id,
      localVehicle?.id,
      detailVehicleId,
      location?.state?.vehicle?.id,
    ];

    for (const candidate of candidates) {
      const normalizedId = parseInt(candidate, 10);
      if (Number.isFinite(normalizedId) && normalizedId > 0) {
        return normalizedId;
      }
    }

    return null;
  }, [
    detailVehicleId,
    localVehicle?.id,
    location?.state?.vehicle?.id,
    mappedVehicleDetail?.id,
    vehicleSingleDetailData?.id,
  ]);
  const canUseVehicleHistoryApi = Boolean(vehicleApiId);
  const canUseVehicleServicesApi = Boolean(vehicleApiId);
  const canUseVehicleTripsApi = Boolean(vehicleApiId);
  const canUseVehicleFuelLogsApi = Boolean(vehicleApiId);
  const canUseVehicleDistanceSummaryApi = Boolean(vehicleApiId);
  const canUseVehicleInstallmentsApi = Boolean(vehicleApiId);
  const canUseVehicleAssignmentHistoryApi = Boolean(vehicleApiId);
  const equipmentDropdownLoading = dropdownLoadingCount > 0;
  const tyreDropdownLoading = tyreDropdownLoadingCount > 0;
  const existingGeneralInfoIds = useMemo(
    () => ({
      makeId: getPositiveNumber(vehicleSingleDetailData?.make_id),
      modelId: getPositiveNumber(vehicleSingleDetailData?.model_id),
      transmissionTypeId: getPositiveNumber(
        vehicleSingleDetailData?.transmission_type,
        vehicleSingleDetailData?.transmission?.id
      ),
      primaryDriverId: getPositiveNumber(
        vehicleSingleDetailData?.primary_driver_id,
        vehicleSingleDetailData?.primary_driver?.id,
        vehicleSingleDetailData?.driver_id,
        vehicleSingleDetailData?.driver?.id,
        vehicleSingleDetailData?.assigned_driver?.id,
        vehicle?.primaryDriver?.id,
        vehicle?.assignedDriver?.id
      ),
      secondaryDriverId: getPositiveNumber(
        vehicleSingleDetailData?.secondary_driver_id,
        vehicleSingleDetailData?.secondary_driver?.id,
        vehicle?.secondaryDriver?.id
      ),
      helperId: getPositiveNumber(
        vehicleSingleDetailData?.helper_id,
        vehicleSingleDetailData?.helper?.id,
        vehicle?.helper?.id
      ),
      branchId: getPositiveNumber(
        vehicleSingleDetailData?.branch_id,
        vehicleSingleDetailData?.branch?.id
      ),
    }),
    [vehicle, vehicleSingleDetailData]
  );

  const initialValues = useMemo(
    () => getInitialValues(vehicle, vehicleSingleDetailData),
    [vehicle, vehicleSingleDetailData]
  );

  useEffect(() => {
    if (isHiredVehicle && activeTab !== "general_info") {
      setActiveTab("general_info");
    }
  }, [activeTab, isHiredVehicle]);

  useEffect(() => {
    if (!detailVehicleId) {
      dispatch(clearVehicleSingleDetailState());
      dispatch(clearVehicleHistoryState());
      dispatch(clearVehicleServiceRecordsState());
      dispatch(clearVehicleOperationalDataState());
      dispatch(clearVehicleInstallmentsState());
      return undefined;
    }

    dispatch(fetchVehicleSingleDetail(detailVehicleId));

    return () => {
      dispatch(clearVehicleSingleDetailState());
      dispatch(clearVehicleHistoryState());
      dispatch(clearVehicleServiceRecordsState());
      dispatch(clearVehicleOperationalDataState());
      dispatch(clearVehicleInstallmentsState());
    };
  }, [detailVehicleId, dispatch]);

  useEffect(() => {
    setComments(Array.isArray(vehicle?.formData?.comments) ? vehicle.formData.comments : []);
    setActivities(
      Array.isArray(vehicle?.formData?.activities) && vehicle.formData.activities.length
        ? vehicle.formData.activities
        : []
    );
    setCommentInput("");
  }, [vehicle?.systemId]);

  useEffect(() => {
    dispatch(clearVehicleHistoryState());
  }, [dispatch, vehicleApiId]);

  useEffect(() => {
    setServicePage(1);
    dispatch(clearVehicleServiceRecordsState());
  }, [dispatch, vehicleApiId]);

  useEffect(() => {
    setTripPage(1);
    setFuelPage(1);
    dispatch(clearVehicleOperationalDataState());
  }, [dispatch, vehicleApiId]);

  useEffect(() => {
    setInstallmentPage(1);
    dispatch(clearVehicleInstallmentsState());
  }, [dispatch, vehicleApiId]);

  useEffect(() => {
    setEquipmentPage(1);
    setLinkedEquipmentDraft("");
    setIsEquipmentSelectionUnlocked(true);
  }, [vehicle?.systemId]);

  useEffect(() => {
    if (activeTab !== "assignment_history" || !canUseVehicleAssignmentHistoryApi) {
      return;
    }

    dispatch(fetchVehicleAssignmentHistory(vehicleApiId));
  }, [activeTab, canUseVehicleAssignmentHistoryApi, dispatch, vehicleApiId]);

  const handleFetchVehicleHistory = useCallback(
    async (historyType = "all") => {
      if (!canUseVehicleHistoryApi) return;

      await dispatch(
        fetchVehicleHistory({
          history_type: historyType,
          vehicle_id: vehicleApiId,
        })
      );
    },
    [canUseVehicleHistoryApi, dispatch, vehicleApiId]
  );

  const handleSubmitVehicleComment = useCallback(
    async ({ comment, parent_id, mention_user_ids }) => {
      if (!canUseVehicleHistoryApi) return;

      await dispatch(
        commentOnVehicle({
          vehicle_id: vehicleApiId,
          comment,
          parent_id,
          mention_user_ids,
        })
      ).unwrap();
    },
    [canUseVehicleHistoryApi, dispatch, vehicleApiId]
  );

  useEffect(() => {
    setShowSwapTyresModal(false);
    setShowAddInstallmentModal(false);
    setSwapTyresLoading(false);
    setSwapTyreForm(createSwapTyreFormState());
    setInstallmentForm(createInstallmentFormState());
  }, [vehicle?.systemId]);

  useEffect(() => {
    if (canUseVehicleInstallmentsApi) {
      setInstallmentData(mapVehicleInstallmentRows(vehicleInstallmentsData));
      return;
    }

    const source = vehicle?.formData?.installmentSchedule;
    if (Array.isArray(source)) {
      setInstallmentData(source);
      return;
    }

    setInstallmentData([]);
  }, [
    canUseVehicleInstallmentsApi,
    vehicle?.formData?.installmentSchedule,
    vehicle?.systemId,
    vehicleInstallmentsData,
  ]);

  const currentPrimaryDriverOption = useMemo(
    () =>
      buildPersonSelectOption({
        id: existingGeneralInfoIds.primaryDriverId,
        label: getPersonLabel(
          vehicleSingleDetailData?.primary_driver,
          vehicleSingleDetailData?.driver,
          vehicleSingleDetailData?.assigned_driver,
          vehicle?.primaryDriver,
          vehicle?.assignedDriver
        ),
        image:
          vehicleSingleDetailData?.primary_driver?.image ||
          vehicleSingleDetailData?.driver?.image ||
          vehicleSingleDetailData?.assigned_driver?.image ||
          vehicle?.primaryDriver?.image ||
          vehicle?.assignedDriver?.image ||
          "",
        status: getPersonStatus(
          vehicleSingleDetailData?.primary_driver,
          vehicleSingleDetailData?.driver,
          vehicleSingleDetailData?.assigned_driver,
          vehicle?.primaryDriver,
          vehicle?.assignedDriver
        ),
      }),
    [
      existingGeneralInfoIds.primaryDriverId,
      vehicle,
      vehicleSingleDetailData,
    ]
  );

  const currentSecondaryDriverOption = useMemo(
    () =>
      buildPersonSelectOption({
        id: existingGeneralInfoIds.secondaryDriverId,
        label: getPersonLabel(
          vehicleSingleDetailData?.secondary_driver,
          vehicle?.secondaryDriver
        ),
        image:
          vehicleSingleDetailData?.secondary_driver?.image ||
          vehicle?.secondaryDriver?.image ||
          "",
        status: getPersonStatus(
          vehicleSingleDetailData?.secondary_driver,
          vehicle?.secondaryDriver
        ),
      }),
    [
      existingGeneralInfoIds.secondaryDriverId,
      vehicle,
      vehicleSingleDetailData,
    ]
  );

  const currentHelperOption = useMemo(
    () =>
      buildPersonSelectOption({
        id: existingGeneralInfoIds.helperId,
        label: getPersonLabel(vehicleSingleDetailData?.helper, vehicle?.helper),
        image:
          vehicleSingleDetailData?.helper?.image ||
          vehicle?.helper?.image ||
          "",
        status: getPersonStatus(vehicleSingleDetailData?.helper, vehicle?.helper),
      }),
    [existingGeneralInfoIds.helperId, vehicle, vehicleSingleDetailData]
  );

  const driverOptions = useMemo(
    () =>
      mergeSelectOptions(driverDropdownOptions, [
        currentPrimaryDriverOption,
        currentSecondaryDriverOption,
      ]),
    [currentPrimaryDriverOption, currentSecondaryDriverOption, driverDropdownOptions]
  );

  const helperOptions = useMemo(
    () => mergeSelectOptions(helperDropdownOptions, [currentHelperOption]),
    [currentHelperOption, helperDropdownOptions]
  );

  const assignmentHistoryEquipmentRecords = useMemo(
    () =>
      Array.isArray(vehicleAssignmentHistoryData?.equipments)
        ? vehicleAssignmentHistoryData.equipments
        : Array.isArray(vehicleAssignmentHistoryData?.data?.equipments)
          ? vehicleAssignmentHistoryData.data.equipments
          : [],
    [vehicleAssignmentHistoryData]
  );

  const assignmentHistoryVehicleAxles = useMemo(
    () =>
      Array.isArray(vehicleAssignmentHistoryData?.vehicleAxles)
        ? vehicleAssignmentHistoryData.vehicleAxles
        : Array.isArray(vehicleAssignmentHistoryData?.data?.vehicleAxles)
          ? vehicleAssignmentHistoryData.data.vehicleAxles
          : [],
    [vehicleAssignmentHistoryData]
  );

  const assignmentHistoryTyreRecords = useMemo(
    () =>
      Array.isArray(vehicleAssignmentHistoryData?.tyres)
        ? vehicleAssignmentHistoryData.tyres
        : Array.isArray(vehicleAssignmentHistoryData?.data?.tyres)
          ? vehicleAssignmentHistoryData.data.tyres
          : [],
    [vehicleAssignmentHistoryData]
  );

  const currentLinkedEquipmentRecord = useMemo(
    () => assignmentHistoryEquipmentRecords.find((item) => Boolean(item?.current)) || null,
    [assignmentHistoryEquipmentRecords]
  );

  const equipmentOptions = useMemo(() => {
    const source = Array.isArray(equipmentDropdownData)
      ? equipmentDropdownData
      : findCollectionByKeys(equipmentDropdownData, [
        "equipments",
        "equipmentdropdown",
        "equipment",
      ]);

    const baseOptions = toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "equipment_id"],
        labelKeys: ["label", "equipment_id", "title", "name", "serial_number"],
        defaultLabelPrefix: "Equipment",
      })
    );
    const currentEquipmentId = getPositiveNumber(currentLinkedEquipmentRecord?.equipment_id);
    const hasCurrentOption = baseOptions.some(
      (option) => getPositiveNumber(option?.optionId, option?.value) === currentEquipmentId
    );
    const fallbackLabel =
      normalizeLinkedValue(vehicle?.linkedEquipmentId) ||
      (currentEquipmentId ? String(currentEquipmentId) : "");

    if (currentEquipmentId && !hasCurrentOption) {
      return [
        {
          label: fallbackLabel || `Equipment ${currentEquipmentId}`,
          value: fallbackLabel || `Equipment ${currentEquipmentId}`,
          optionId: currentEquipmentId,
        },
        ...baseOptions,
      ];
    }

    return baseOptions;
  }, [currentLinkedEquipmentRecord?.equipment_id, equipmentDropdownData, vehicle?.linkedEquipmentId]);

  const currentLinkedEquipmentOption = useMemo(() => {
    const currentEquipmentId = getPositiveNumber(currentLinkedEquipmentRecord?.equipment_id);
    if (!currentEquipmentId) return null;

    return (
      equipmentOptions.find(
        (option) => getPositiveNumber(option?.optionId, option?.value) === currentEquipmentId
      ) || null
    );
  }, [currentLinkedEquipmentRecord?.equipment_id, equipmentOptions]);

  const draftLinkedEquipmentOption = useMemo(
    () => findOption(equipmentOptions, linkedEquipmentDraft),
    [equipmentOptions, linkedEquipmentDraft]
  );

  const preferredSwapSourceType = useMemo(
    () => normalizeSwapAssetType(vehicleSingleDetailData?.asset_type),
    [vehicleSingleDetailData?.asset_type]
  );

  const preferredSwapSourceAssetId = useMemo(() => {
    const requestedAssetType = normalizeSwapAssetType(vehicleSingleDetailData?.asset_type);
    if (requestedAssetType === "equipment") {
      return String(
        getPositiveNumber(
          vehicleSingleDetailData?.asset_id,
          currentLinkedEquipmentRecord?.equipment_id,
          currentLinkedEquipmentOption?.optionId,
          currentLinkedEquipmentOption?.id
        ) || ""
      );
    }

    return String(getPositiveNumber(vehicleSingleDetailData?.asset_id, vehicleApiId) || "");
  }, [
    currentLinkedEquipmentOption?.id,
    currentLinkedEquipmentOption?.optionId,
    currentLinkedEquipmentRecord?.equipment_id,
    vehicleApiId,
    vehicleSingleDetailData?.asset_id,
    vehicleSingleDetailData?.asset_type,
  ]);

  const sourceAssetTyreOptions = useMemo(() => {
    const requestType = normalizeSwapAssetType(assetTyresRequest?.asset_type);
    const requestAssetId = String(assetTyresRequest?.asset_id || "");
    if (
      requestType !== normalizeSwapAssetType(swapTyreForm.sourceType) ||
      requestAssetId !== String(swapTyreForm.sourceAssetId || "")
    ) {
      return [];
    }

    return mapAssetTyreOptions(assetTyresData);
  }, [
    assetTyresData,
    assetTyresRequest?.asset_id,
    assetTyresRequest?.asset_type,
    swapTyreForm.sourceAssetId,
    swapTyreForm.sourceType,
  ]);

  useEffect(() => {
    if (currentLinkedEquipmentOption?.value) {
      setLinkedEquipmentDraft(currentLinkedEquipmentOption.value);
      setIsEquipmentSelectionUnlocked(false);
      return;
    }

    if (canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryData) {
      setLinkedEquipmentDraft("");
      setIsEquipmentSelectionUnlocked(true);
      return;
    }

    const fallbackLinkedEquipment = normalizeLinkedValue(vehicle?.linkedEquipmentId);
    if (fallbackLinkedEquipment) {
      setLinkedEquipmentDraft((previous) => previous || fallbackLinkedEquipment);
    }
    setIsEquipmentSelectionUnlocked(true);
  }, [
    canUseVehicleAssignmentHistoryApi,
    currentLinkedEquipmentOption?.value,
    vehicle?.linkedEquipmentId,
    vehicleAssignmentHistoryData,
  ]);

  useEffect(() => {
    if (!showSwapTyresModal) return;

    const sourceAssetId = getPositiveNumber(swapTyreForm.sourceAssetId);
    const sourceAssetType = normalizeSwapAssetType(swapTyreForm.sourceType);
    if (!sourceAssetId) return;

    dispatch(
      fetchAssetTyres({
        asset_id: sourceAssetId,
        asset_type: sourceAssetType,
      })
    );
  }, [dispatch, showSwapTyresModal, swapTyreForm.sourceAssetId, swapTyreForm.sourceType]);

  useEffect(() => {
    if (!showSwapTyresModal) return;

    setSwapTyreForm((previous) => {
      if (
        normalizeSwapAssetType(previous.sourceType) !== normalizeSwapAssetType(swapTyreForm.sourceType) ||
        String(previous.sourceAssetId || "") !== String(swapTyreForm.sourceAssetId || "")
      ) {
        return previous;
      }

      if (!sourceAssetTyreOptions.length) {
        return previous.sourcePosition ? { ...previous, sourcePosition: "" } : previous;
      }

      const hasCurrentSelection = sourceAssetTyreOptions.some(
        (option) => String(option?.value || "") === String(previous.sourcePosition || "")
      );
      return hasCurrentSelection || !previous.sourcePosition
        ? previous
        : { ...previous, sourcePosition: "" };
    });
  }, [
    showSwapTyresModal,
    sourceAssetTyreOptions,
    swapTyreForm.sourceAssetId,
    swapTyreForm.sourceType,
  ]);

  useEffect(() => {
    if (!showSwapTyresModal) {
      setTargetAssetTyreOptions([]);
      setTargetAssetTyresLoading(false);
      return undefined;
    }

    const targetAssetId = getPositiveNumber(swapTyreForm.targetAssetId);
    const targetAssetType = normalizeSwapAssetType(swapTyreForm.targetType);
    if (!targetAssetId || !String(swapTyreForm.targetType || "").trim()) {
      setTargetAssetTyreOptions([]);
      setTargetAssetTyresLoading(false);
      return undefined;
    }

    let isActive = true;
    setTargetAssetTyresLoading(true);
    setTargetAssetTyreOptions([]);

    apiService
      .getAssetTyres({
        asset_id: targetAssetId,
        asset_type: targetAssetType,
      })
      .then((response) => {
        if (!isActive) return;
        setTargetAssetTyreOptions(mapAssetTyreOptions(response?.data));
      })
      .catch(() => {
        if (!isActive) return;
        setTargetAssetTyreOptions([]);
      })
      .finally(() => {
        if (!isActive) return;
        setTargetAssetTyresLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [showSwapTyresModal, swapTyreForm.targetAssetId, swapTyreForm.targetType]);

  const vehicleSwapAssetOptions = useMemo(
    () => {
      const source = Array.isArray(vehiclesDropdownData)
        ? vehiclesDropdownData
        : findCollectionByKeys(vehiclesDropdownData, [
          "vehicles",
          "vehicledropdown",
          "vehicle",
        ]);

      const baseOptions = toLabelValueOptions(
        normalizeOptions(source, {
          valueKeys: ["value", "id", "vehicle_id"],
          labelKeys: ["label", "vehicle_id", "registration_number", "title", "name"],
          defaultLabelPrefix: "Vehicle",
        })
      ).map((option) => ({
        ...option,
        systemId: String(
          option?.systemId ||
          option?.label ||
          option?.vehicle_id ||
          option?.registration_number ||
          option?.value ||
          ""
        ).trim(),
        registrationNumber: String(
          option?.registrationNumber ||
          option?.registration_number ||
          option?.label ||
          option?.value ||
          ""
        ).trim(),
      }));

      if (baseOptions.length) return baseOptions;

      return allVehicles.map((row) => ({
        label: row?.systemId || "-",
        value: row?.systemId || "",
        registrationNumber: row?.registrationNumber || "",
        systemId: row?.systemId || "",
      }));
    },
    [allVehicles, vehiclesDropdownData]
  );

  const equipmentSwapAssetOptions = useMemo(
    () => {
      if (equipmentOptions.length) {
        return equipmentOptions.map((option) => ({
          ...option,
          systemId: String(
            option?.systemId ||
            option?.equipment_id ||
            option?.label ||
            option?.value ||
            ""
          ).trim(),
          linkedVehicleId: String(
            option?.linkedVehicleId || option?.linked_vehicle_id || ""
          ).trim(),
        }));
      }

      return allEquipments.map((row) => ({
        label: row?.systemId || "-",
        value: row?.systemId || "",
        systemId: row?.systemId || "",
        linkedVehicleId: row?.linkedVehicleId || "",
      }));
    },
    [allEquipments, equipmentOptions]
  );

  const tyreOptions = useMemo(() => {
    const source = Array.isArray(tyresDropdownData)
      ? tyresDropdownData
      : findCollectionByKeys(tyresDropdownData, [
        "tyres",
        "tyre",
        "tyresdropdown",
        "tyredropdown",
        "tyrelist",
      ]);

    const baseOptions = toLabelValueOptions(
      normalizeOptions(source, {
        valueKeys: ["value", "id", "tyre_id", "system_id"],
        labelKeys: ["label", "tyres_id", "tyre_id", "serial_number", "system_id", "name", "title"],
        defaultLabelPrefix: "Tyre",
      })
    );

    const assignedTyreIds = new Set();
    assignmentHistoryVehicleAxles.forEach((axle) => {
      [
        axle?.outer_left_tyre_id,
        axle?.outer_right_tyre_id,
        axle?.inner_left_tyre_id,
        axle?.inner_right_tyre_id,
        axle?.left_tyre_id,
        axle?.right_tyre_id,
        axle?.left_inner_tyre_id,
        axle?.left_outer_tyre_id,
        axle?.right_inner_tyre_id,
        axle?.right_outer_tyre_id,
      ].forEach((value) => {
        const tyreId = getPositiveNumber(value);
        if (tyreId) assignedTyreIds.add(tyreId);
      });
    });

    const fallbackOptions = Array.from(assignedTyreIds)
      .filter(
        (tyreId) =>
          !baseOptions.some(
            (option) => getPositiveNumber(option?.optionId, option?.id, option?.value) === tyreId
          )
      )
      .map((tyreId) => ({
        label: `TY-${tyreId}`,
        value: String(tyreId),
        optionId: tyreId,
      }));

    if (baseOptions.length || fallbackOptions.length) {
      return [...fallbackOptions, ...baseOptions];
    }

    return allTyres.map((row) => ({
      label: row?.systemId || "-",
      value: row?.systemId || "",
    }));
  }, [allTyres, assignmentHistoryVehicleAxles, tyresDropdownData]);


  const serviceRows = useMemo(() => {
    if (canUseVehicleServicesApi) {
      return mapVehicleServiceRows(vehicleServiceRecordsData);
    }

    const source = vehicle?.formData?.serviceRecords;
    if (Array.isArray(source)) return source;
    return [];
  }, [
    canUseVehicleServicesApi,
    vehicle?.formData?.serviceRecords,
    vehicleServiceRecordsData,
  ]);

  const tyreHistoryData = useMemo(() => {
    if (canUseVehicleAssignmentHistoryApi) {
      if (!vehicleAssignmentHistoryData) return [];

      return mapVehicleTyreHistoryRows(assignmentHistoryTyreRecords, {
        tyreOptions,
      });
    }

    const source = vehicle?.formData?.tyreHistory;
    if (Array.isArray(source)) return source;
    return [];
  }, [
    assignmentHistoryTyreRecords,
    canUseVehicleAssignmentHistoryApi,
    tyreOptions,
    vehicle?.formData?.tyreHistory,
    vehicleAssignmentHistoryData,
  ]);

  const assignmentEquipmentHistoryData = useMemo(() => {
    if (canUseVehicleAssignmentHistoryApi) {
      if (!vehicleAssignmentHistoryData) return [];

      return mapVehicleEquipmentHistoryRows(assignmentHistoryEquipmentRecords, {
        equipmentOptions,
      });
    }

    const source = vehicle?.formData?.equipmentHistory;
    if (Array.isArray(source)) return source;
    return [];
  }, [
    canUseVehicleAssignmentHistoryApi,
    equipmentOptions,
    vehicle?.formData?.equipmentHistory,
    assignmentHistoryEquipmentRecords,
    vehicleAssignmentHistoryData,
  ]);

  const assignmentVehicleAxleConfigurations = useMemo(() => {
    if (canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryData) {
      return mapVehicleAxleConfigurations(assignmentHistoryVehicleAxles);
    }

    const source = vehicle?.formData?.axleConfigurations;
    return Array.isArray(source) ? source : [];
  }, [
    canUseVehicleAssignmentHistoryApi,
    vehicle?.formData?.axleConfigurations,
    assignmentHistoryVehicleAxles,
    vehicleAssignmentHistoryData,
  ]);

  const linkedTyreAssignmentFields = useMemo(() => {
    const linkedFields = new Set();

    assignmentVehicleAxleConfigurations.forEach((axle, index) => {
      const axleKey = String(axle?.axleId || `A${index + 1}`);

      Object.entries(axle?.assignments || {}).forEach(([fieldKey, value]) => {
        if (normalizeLinkedValue(value)) {
          linkedFields.add(`${axleKey}:${fieldKey}`);
        }
      });
    });

    return linkedFields;
  }, [assignmentVehicleAxleConfigurations]);

  const tripHistoryData = useMemo(() => {
    if (canUseVehicleTripsApi) {
      return mapVehicleTripRows(vehicleTripsData);
    }

    const source = vehicle?.formData?.tripHistory;
    if (Array.isArray(source)) return source;
    return [];
  }, [
    canUseVehicleTripsApi,
    vehicle?.formData?.tripHistory,
    vehicleTripsData,
  ]);

  const fuelLogData = useMemo(() => {
    if (canUseVehicleFuelLogsApi) {
      return mapVehicleFuelLogRows(vehicleFuelLogsData, {
        stationOptions: fuelStationOptions,
      });
    }

    const source = vehicle?.formData?.fuelLogs;
    if (Array.isArray(source)) return source;
    return [];
  }, [
    canUseVehicleFuelLogsApi,
    fuelStationOptions,
    vehicle?.formData?.fuelLogs,
    vehicleFuelLogsData,
  ]);

  const paginatedServiceRows = useMemo(() => {
    if (canUseVehicleServicesApi) return serviceRows;

    const start = (servicePage - 1) * serviceLimit;
    return serviceRows.slice(start, start + serviceLimit);
  }, [canUseVehicleServicesApi, serviceRows, servicePage, serviceLimit]);

  const serviceTotalItems = useMemo(() => {
    if (!canUseVehicleServicesApi) return serviceRows.length;

    return (
      Number(vehicleServiceRecordsMeta?.totalItems) ||
      Number(vehicleServiceRecordsMeta?.itemCount) ||
      serviceRows.length
    );
  }, [canUseVehicleServicesApi, serviceRows.length, vehicleServiceRecordsMeta]);

  const serviceTotalPages = useMemo(() => {
    if (!canUseVehicleServicesApi) {
      return Math.max(1, Math.ceil(serviceRows.length / Math.max(serviceLimit, 1)));
    }

    return Math.max(
      1,
      Number(vehicleServiceRecordsMeta?.totalPages) ||
      Math.ceil(serviceTotalItems / Math.max(serviceLimit, 1))
    );
  }, [
    canUseVehicleServicesApi,
    serviceLimit,
    serviceRows.length,
    serviceTotalItems,
    vehicleServiceRecordsMeta,
  ]);

  useEffect(() => {
    if (!canUseVehicleServicesApi) return;

    dispatch(
      fetchVehicleServiceRecords({
        vehicle_id: vehicleApiId,
        page: servicePage,
        limit: serviceLimit,
      })
    );
  }, [
    canUseVehicleServicesApi,
    dispatch,
    serviceLimit,
    servicePage,
    vehicleApiId,
  ]);

  useEffect(() => {
    if (!canUseVehicleTripsApi) return;

    dispatch(
      fetchVehicleTrips({
        vehicle_id: vehicleApiId,
        page: tripPage,
        limit: tripLimit,
      })
    );
  }, [
    canUseVehicleTripsApi,
    dispatch,
    tripLimit,
    tripPage,
    vehicleApiId,
  ]);

  useEffect(() => {
    if (!canUseVehicleFuelLogsApi) return;

    dispatch(
      fetchVehicleFuelLogs({
        vehicle_id: vehicleApiId,
        page: fuelPage,
        limit: fuelLimit,
      })
    );
  }, [
    canUseVehicleFuelLogsApi,
    dispatch,
    fuelLimit,
    fuelPage,
    vehicleApiId,
  ]);

  useEffect(() => {
    if (!canUseVehicleDistanceSummaryApi) return;

    dispatch(fetchVehicleDistanceSummary(vehicleApiId));
  }, [
    canUseVehicleDistanceSummaryApi,
    dispatch,
    vehicleApiId,
  ]);

  useEffect(() => {
    if (!canUseVehicleInstallmentsApi) return;

    dispatch(fetchVehicleInstallments(vehicleApiId));
  }, [
    canUseVehicleInstallmentsApi,
    dispatch,
    vehicleApiId,
  ]);

  const paginatedInstallmentRows = useMemo(() => {
    const start = (installmentPage - 1) * installmentLimit;
    return installmentData.slice(start, start + installmentLimit);
  }, [installmentData, installmentLimit, installmentPage]);

  const paginatedTripRows = useMemo(() => {
    if (canUseVehicleTripsApi) return tripHistoryData;

    const start = (tripPage - 1) * tripLimit;
    return tripHistoryData.slice(start, start + tripLimit);
  }, [canUseVehicleTripsApi, tripHistoryData, tripLimit, tripPage]);

  const paginatedFuelRows = useMemo(() => {
    if (canUseVehicleFuelLogsApi) return fuelLogData;

    const start = (fuelPage - 1) * fuelLimit;
    return fuelLogData.slice(start, start + fuelLimit);
  }, [canUseVehicleFuelLogsApi, fuelLogData, fuelLimit, fuelPage]);

  const tripTotalItems = useMemo(() => {
    if (!canUseVehicleTripsApi) return tripHistoryData.length;

    return (
      Number(vehicleTripsMeta?.totalItems) ||
      Number(vehicleTripsMeta?.itemCount) ||
      tripHistoryData.length
    );
  }, [canUseVehicleTripsApi, tripHistoryData.length, vehicleTripsMeta]);

  const tripTotalPages = useMemo(() => {
    if (!canUseVehicleTripsApi) {
      return Math.max(1, Math.ceil(tripHistoryData.length / Math.max(tripLimit, 1)));
    }

    return Math.max(
      1,
      Number(vehicleTripsMeta?.totalPages) ||
      Math.ceil(tripTotalItems / Math.max(tripLimit, 1))
    );
  }, [canUseVehicleTripsApi, tripHistoryData.length, tripLimit, tripTotalItems, vehicleTripsMeta]);

  const fuelTotalItems = useMemo(() => {
    if (!canUseVehicleFuelLogsApi) return fuelLogData.length;

    return (
      Number(vehicleFuelLogsMeta?.totalItems) ||
      Number(vehicleFuelLogsMeta?.itemCount) ||
      fuelLogData.length
    );
  }, [canUseVehicleFuelLogsApi, fuelLogData.length, vehicleFuelLogsMeta]);

  const fuelTotalPages = useMemo(() => {
    if (!canUseVehicleFuelLogsApi) {
      return Math.max(1, Math.ceil(fuelLogData.length / Math.max(fuelLimit, 1)));
    }

    return Math.max(
      1,
      Number(vehicleFuelLogsMeta?.totalPages) ||
      Math.ceil(fuelTotalItems / Math.max(fuelLimit, 1))
    );
  }, [canUseVehicleFuelLogsApi, fuelLogData.length, fuelLimit, fuelTotalItems, vehicleFuelLogsMeta]);

  const paginatedTyreRows = useMemo(() => {
    const start = (tyrePage - 1) * tyreLimit;
    return tyreHistoryData.slice(start, start + tyreLimit);
  }, [tyreHistoryData, tyrePage, tyreLimit]);

  const assetHistoryItems = useMemo(
    () =>
      [...activities, ...comments].sort(
        (a, b) => normalizeNumber(b?.createdAtTs, 0) - normalizeNumber(a?.createdAtTs, 0)
      ),
    [activities, comments]
  );


  const totalOperationalKm = useMemo(
    () => tripHistoryData.reduce((sum, row) => sum + normalizeNumber(row.distance), 0),
    [tripHistoryData]
  );

  const operationalSummary = useMemo(() => {
    if (canUseVehicleDistanceSummaryApi && vehicleDistanceSummaryData) {
      return {
        totalKm: normalizeNumber(
          vehicleDistanceSummaryData?.total_km,
          totalOperationalKm
        ),
        averageKmPerMonth: normalizeNumber(
          vehicleDistanceSummaryData?.average_km_per_month,
          0
        ),
        last30DaysKm: normalizeNumber(
          vehicleDistanceSummaryData?.last_30_days_km,
          0
        ),
      };
    }

    const source = vehicle?.formData?.operationalSummary || {};
    if (hasVehicleDetailSource && !Object.keys(source).length) {
      return {
        totalKm: normalizeNumber(vehicle?.currentOdometerValue, totalOperationalKm),
        averageKmPerMonth: normalizeNumber(source?.averageKmPerMonth, 0),
        last30DaysKm: normalizeNumber(source?.last30DaysKm, 0),
      };
    }

    return {
      totalKm: normalizeNumber(source.totalKm, totalOperationalKm),
      averageKmPerMonth: normalizeNumber(source.averageKmPerMonth, 50),
      last30DaysKm: normalizeNumber(source.last30DaysKm, 20),
    };
  }, [
    canUseVehicleDistanceSummaryApi,
    hasVehicleDetailSource,
    totalOperationalKm,
    vehicle?.currentOdometerValue,
    vehicle?.formData?.operationalSummary,
    vehicleDistanceSummaryData,
  ]);

  const serviceColumns = [
    { name: "Service Entry ID", selector: (row) => <span className="hyperlink" title={row.id}>{row.id}</span>, tableColumn: false, minWidth: "155px" },
    { name: "Work Order ID", selector: (row) => <span className="hyperlink" title={row.workOrderId}>{row.workOrderId}</span>, tableColumn: false, minWidth: "145px" },
    { name: "Service Type", selector: (row) => row.serviceType, tableColumn: false, minWidth: "130px" },
    { name: "Service Date", selector: (row) => row.serviceDate, tableColumn: false, minWidth: "130px" },
    { name: "Odometer", selector: (row) => row.odometer, tableColumn: false, minWidth: "110px" },
    { name: "Part Cost (PKR)", selector: (row) => formatAmount(row.partCost), tableColumn: false, minWidth: "145px" },
    { name: "Labour Cost (PKR)", selector: (row) => formatAmount(row.labourCost), tableColumn: false, minWidth: "160px" },
    { name: "Total Cost (PKR)", selector: (row) => formatAmount(row.totalCost), tableColumn: false, minWidth: "145px" },
    {
      name: "Status",
      selector: (row) => {
        const meta = serviceStatusMeta(row.status);
        return <span className="asset_vehicle_table_status" style={{ color: meta.color }}>{meta.icon}{meta.label}</span>;
      },
      tableColumn: false,
      minWidth: "130px",
    },
  ];

  const equipmentColumns = [
    { name: "Equipment ID", selector: (row) => <span className="hyperlink" title={row.equipmentId}>{row.equipmentId}</span>, tableColumn: false, minWidth: "120px" },
    { name: "Start Date", selector: (row) => row.startDate, tableColumn: false, minWidth: "120px" },
    { name: "End Date", selector: (row) => row.endDate, tableColumn: false, minWidth: "120px" },
    { name: "Duration", selector: (row) => row.duration, tableColumn: false, minWidth: "220px" },
  ];

  const tyreColumns = [
    { name: "Tyre ID", selector: (row) => <span className="hyperlink" title={row.tyreId}>{row.tyreId}</span>, tableColumn: false, minWidth: "120px" },
    { name: "Position", selector: (row) => row.position, tableColumn: false, minWidth: "140px" },
    { name: "Installed Date", selector: (row) => row.installedDate, tableColumn: false, minWidth: "145px" },
    { name: "Removed Date", selector: (row) => row.removedDate, tableColumn: false, minWidth: "145px" },
    { name: "Start Odometer", selector: (row) => row.startOdometer, tableColumn: false, minWidth: "150px" },
    { name: "End Odometer", selector: (row) => row.endOdometer, tableColumn: false, minWidth: "145px" },
    { name: "KM Driven", selector: (row) => row.kmDriven || "-", tableColumn: false, minWidth: "130px" },
  ];

  const installmentColumns = [
    { name: "Installment #", selector: (row) => row.number, tableColumn: false, minWidth: "125px" },
    { name: "Payment Method", selector: (row) => row.paymentMethod || "Bank Transfer", tableColumn: false, minWidth: "170px" },
    { name: "Amount (PKR)", selector: (row) => formatAmount(row.amount), tableColumn: false, minWidth: "135px" },
    {
      name: "Submission Date",
      selector: (row) => row.submissionDate || row.dueDate,
      tableColumn: false,
      minWidth: "150px",
    },
    {
      name: "Attachments",
      selector: (row) =>
        row.attachmentsCount ? (
          <button
            type="button"
            className="d-flex align-items-center gap-1"
            style={{
              color: "#3d18c3",
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
            }}
            onClick={(event) => {
              event.stopPropagation();
              openAttachmentViewer(row.attachments);
            }}
            title="View attachments"
          >
            <FiPaperclip /> {row.attachmentsCount}
          </button>
        ) : (
          ""
        ),
      tableColumn: false,
      minWidth: "130px",
      ignoreRowClick: true,
    },
  ];

  const tripColumns = [
    { name: "Trip ID", selector: (row) => <span className="hyperlink">{row.tripId}</span>, tableColumn: false, minWidth: "120px" },
    { name: "Driver", selector: (row) => <div className="asset_driver_cell"><UserAvatar username={row.driver} image={row.driverImage || ""} /><span className="asset_driver_name">{row.driver}</span></div>, tableColumn: false, minWidth: "170px" },
    { name: "Due Date", selector: (row) => row.dueDate, tableColumn: false, minWidth: "125px" },
    { name: "End Date", selector: (row) => row.endDate, tableColumn: false, minWidth: "125px" },
    { name: "Distance (km)", selector: (row) => row.distance, tableColumn: false, minWidth: "130px" },
    { name: "Fuel Used (ltrs)", selector: (row) => row.fuelUsed, tableColumn: false, minWidth: "150px" },
  ];

  const fuelColumns = [
    { name: "Log ID", selector: (row) => <span className="hyperlink">{row.logId}</span>, tableColumn: false, minWidth: "120px" },
    { name: "Date", selector: (row) => row.date, tableColumn: false, minWidth: "110px" },
    { name: "Odometer", selector: (row) => row.odometer, tableColumn: false, minWidth: "115px" },
    { name: "Fuel (ltrs)", selector: (row) => row.litres, tableColumn: false, minWidth: "110px" },
    { name: "Cost (PKR)", selector: (row) => formatAmount(row.cost), tableColumn: false, minWidth: "120px" },
    { name: "Supplier", selector: (row) => <span className="hyperlink" title={row.supplier}>{row.supplier}</span>, tableColumn: false, minWidth: "150px" },
  ];

  const handleCommentSend = () => {
    const plainText = stripHtml(commentInput);
    if (!plainText) return;
    const createdAtTs = Date.now();
    const createdAt = new Date(createdAtTs).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    const commentItem = {
      id: `comment-${createdAtTs}`,
      type: "comment",
      user: currentUser?.username || currentUser?.name || "User",
      image: getUserImage(currentUser),
      text: commentInput,
      time: createdAt,
      createdAtTs,
    };
    setComments((previous) => [commentItem, ...previous]);
    setCommentInput("");
  };

  if (detailVehicleId && !vehicle && !vehicleSingleDetailError) {
    return <div className="loading_spinner"><Spinner animation="grow" /></div>;
  }

  if (!vehicle) {
    return (
      <div className="assets_vehicle_details_page">
        <Breadcrumbs values={["Assets", "Vehicles", "Vehicle Details"]} />
        <div className="page_container"><div className="table_no_records">Vehicle not found</div></div>
      </div>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={
        isHiredVehicle
          ? threePlVehicleDetailsSchema
          : activeTab === "general_info"
          ? detailsSchema
          : activeTab === "financial_insurance"
            ? financialInsuranceSchema
            : Yup.object().shape({})
      }
      enableReinitialize
      onSubmit={async (values, { setSubmitting }) => {
        if (!canUpdate) {
          setSubmitting(false);
          errorNotify("You do not have permission to update vehicles.");
          return;
        }

        if (!vehicleApiId) {
          setSubmitting(false);
          errorNotify("Vehicle ID is missing");
          return;
        }

        if (isHiredVehicle) {
          try {
            await apiService.updateThreePlVehicle(
              buildThreePlVehiclePayload(values, vehicleApiId)
            );
            setSubmitting(false);
            successNotify("Hired / Contract vehicle updated successfully");
            navigate(returnPath);
          } catch (error) {
            setSubmitting(false);
            errorNotify(error || "Failed to update Hired / Contract vehicle");
          }
          return;
        }

        if (activeTab === "financial_insurance") {
          const initialPurchaseSnapshot = buildVehiclePurchaseInfoSnapshot(
            vehicleApiId,
            initialValues
          );
          const currentPurchaseSnapshot = buildVehiclePurchaseInfoSnapshot(
            vehicleApiId,
            values
          );
          const purchasePayload = {
            ...currentPurchaseSnapshot,
            updatedKeys: getUpdatedVehiclePurchaseInfoKeys(
              currentPurchaseSnapshot,
              initialPurchaseSnapshot
            ),
          };

          try {
            await dispatch(updateVehiclePurchaseInfo(purchasePayload)).unwrap();
            setSubmitting(false);
            successNotify("Vehicle financial & insurance details updated successfully");
            navigate(returnPath);
          } catch (error) {
            setSubmitting(false);
            errorNotify(error || "Failed to update vehicle financial & insurance details");
          }
          return;
        }

        const resolvedIds = {
          makeId: resolveSelectOptionId(makeApiOptions, values.make, {
            fallbackId: existingGeneralInfoIds.makeId,
            initialValue: initialValues.make,
          }),
          modelId: resolveSelectOptionId(modelApiOptions, values.model, {
            fallbackId: existingGeneralInfoIds.modelId,
            initialValue: initialValues.model,
          }),
          transmissionTypeId: resolveSelectOptionId(
            transmissionApiOptions,
            values.transmissionType,
            {
              fallbackId: existingGeneralInfoIds.transmissionTypeId,
              initialValue: initialValues.transmissionType,
            }
          ),
          branchId: resolveSelectOptionId(branchApiOptions, values.branch, {
            fallbackId: existingGeneralInfoIds.branchId,
            initialValue: initialValues.branch,
          }),
          primaryDriverId: resolveSelectOptionId(driverOptions, values.primaryDriver, {
            fallbackId: existingGeneralInfoIds.primaryDriverId,
            initialValue: initialValues.primaryDriver,
          }),
          secondaryDriverId: resolveSelectOptionId(driverOptions, values.secondaryDriver, {
            fallbackId: existingGeneralInfoIds.secondaryDriverId,
            initialValue: initialValues.secondaryDriver,
          }),
          helperId: resolveSelectOptionId(helperOptions, values.helper, {
            fallbackId: existingGeneralInfoIds.helperId,
            initialValue: initialValues.helper,
          }),
        };

        const unresolvedSelections = [
          !resolvedIds.makeId ? "Make" : null,
          !resolvedIds.modelId ? "Model" : null,
          !resolvedIds.transmissionTypeId ? "Transmission Type" : null,
          !resolvedIds.branchId ? "Branch" : null,
          !resolvedIds.primaryDriverId ? "Primary Driver" : null,
        ].filter(Boolean);

        if (unresolvedSelections.length) {
          setSubmitting(false);
          errorNotify(
            `Please select valid ${unresolvedSelections.join(", ")} values before saving`
          );
          return;
        }

        const initialSnapshot = buildVehicleGeneralInfoSnapshot({
          vehicleId: vehicleApiId,
          values: initialValues,
          attachments: initialValues.attachments || [],
          makeId: existingGeneralInfoIds.makeId,
          modelId: existingGeneralInfoIds.modelId,
          transmissionTypeId: existingGeneralInfoIds.transmissionTypeId,
          primaryDriverId: existingGeneralInfoIds.primaryDriverId,
          secondaryDriverId: existingGeneralInfoIds.secondaryDriverId,
          helperId: existingGeneralInfoIds.helperId,
          branchId: existingGeneralInfoIds.branchId,
        });
        const currentSnapshot = buildVehicleGeneralInfoSnapshot({
          vehicleId: vehicleApiId,
          values,
          attachments: values.attachments || [],
          makeId: resolvedIds.makeId,
          modelId: resolvedIds.modelId,
          transmissionTypeId: resolvedIds.transmissionTypeId,
          primaryDriverId: resolvedIds.primaryDriverId,
          secondaryDriverId: resolvedIds.secondaryDriverId,
          helperId: resolvedIds.helperId,
          branchId: resolvedIds.branchId,
        });
        const payload = {
          ...currentSnapshot,
          updatedKeys: getUpdatedVehicleGeneralInfoKeys(currentSnapshot, initialSnapshot),
        };

        try {
          await dispatch(updateVehicleGeneralInfo(payload)).unwrap();
          setSubmitting(false);
          successNotify("Vehicle details updated successfully");
          navigate(returnPath);
        } catch (error) {
          setSubmitting(false);
          errorNotify(error || "Failed to update vehicle details");
        }
      }}
    >
      {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, setFieldTouched, isSubmitting }) => {
        const currentStatusMeta = statusMeta(values.status);
        const makeSelectOptions = ensureCurrentOption(makeApiOptions, values.make);
        const modelSelectOptions = ensureCurrentOption(modelApiOptions, values.model);
        const transmissionSelectOptions = ensureCurrentOption(
          transmissionApiOptions,
          values.transmissionType
        );
        const branchSelectOptions = ensureCurrentOption(branchApiOptions, values.branch);
        const threePlTransporterSelectOptions = ensureCurrentIdOption(
          threePlTransporterOptions,
          values.transporter,
          values.transporterName
        );
        const threePlVehicleTypeSelectOptions = ensureCurrentIdOption(
          threePlVehicleTypeOptions,
          values.vehicleType,
          vehicle?.vehicleType || values.vehicleType
        );
        const purchaseCost = normalizeNumber(values.purchaseCost, vehicle?.totalCost || 0);
        const currentAttachmentUrl =
          attachmentViewer.urls[attachmentViewer.currentIndex] || "";
        const currentAttachmentName = getInstallmentAttachmentName(currentAttachmentUrl);
        const hasMultipleAttachments = attachmentViewer.urls.length > 1;
        const insuranceAnnualCost = normalizeNumber(
          values.insuranceCost,
          normalizeNumber(values.premiumAmount, 0)
        );
        const maintenanceAnnualCost =
          normalizeNumber(values.maintenanceCost, 0) ||
          serviceRows.reduce((sum, row) => sum + normalizeNumber(row.totalCost), 0);
        const fuelAnnualCost = fuelLogData.reduce(
          (sum, row) => sum + normalizeNumber(row.cost),
          0
        );
        const totalRunningCost =
          purchaseCost + insuranceAnnualCost + maintenanceAnnualCost + fuelAnnualCost;
        const showCostSummary = totalRunningCost > 0;
        const avgCost = totalOperationalKm
          ? Math.round((maintenanceAnnualCost + fuelAnnualCost) / totalOperationalKm)
          : 0;
        const insuranceProviderSelectOptions = ensureCurrentOption(
          insuranceProviderOptions,
          values.insuranceProvider
        );
        const linkedEquipmentValue = isEquipmentSelectionUnlocked
          ? draftLinkedEquipmentOption ||
          findOption(equipmentOptions, values.linkedEquipmentId) ||
          null
          : currentLinkedEquipmentOption ||
          draftLinkedEquipmentOption ||
          findOption(equipmentOptions, values.linkedEquipmentId) ||
          null;
        const draftAxleConfigurationsById = new Map(
          (values.axleConfigurations || []).map((axle, index) => [
            String(axle?.axleId || `A${index + 1}`),
            axle,
          ])
        );
        const axleFormIndexById = new Map(
          (values.axleConfigurations || []).map((axle, index) => [
            String(axle?.axleId || `A${index + 1}`),
            index,
          ])
        );
        const displayedAxleConfigurations = assignmentVehicleAxleConfigurations.length
          ? assignmentVehicleAxleConfigurations.map((axle, index) => {
            const draftAxleConfiguration =
              draftAxleConfigurationsById.get(String(axle?.axleId || `A${index + 1}`)) || {};

            return {
              ...axle,
              tyreConfiguration: hasMeaningfulValue(draftAxleConfiguration?.tyreConfiguration)
                ? draftAxleConfiguration.tyreConfiguration
                : axle?.tyreConfiguration,
              assignments: mergeNonEmptyValues(
                axle?.assignments || {},
                draftAxleConfiguration?.assignments || {}
              ),
            };
          })
          : values.axleConfigurations || [];
        const shouldShowAssignmentPagination =
          assignmentEquipmentHistoryData.length > equipmentLimit;
        const assignmentEquipmentRows = shouldShowAssignmentPagination
          ? assignmentEquipmentHistoryData.slice(
            (equipmentPage - 1) * equipmentLimit,
            equipmentPage * equipmentLimit
          )
          : assignmentEquipmentHistoryData;
        const selectedEquipmentApiId = getPositiveNumber(
          linkedEquipmentValue?.optionId,
          linkedEquipmentValue?.id,
          linkedEquipmentValue?.value
        );
        const shouldShowTyrePagination = tyreHistoryData.length > tyreLimit;
        const paginatedAssignmentTyreRows = shouldShowTyrePagination
          ? paginatedTyreRows
          : tyreHistoryData;
        const tyreLinkSelections = buildVehicleTyreLinkSelections(
          displayedAxleConfigurations,
          tyreOptions
        );
        const selectedTyreIdsByField = new Map();
        tyreLinkSelections.forEach(({ axleId, selections = [] }) => {
          selections.forEach(({ fieldKey, resolvedTyreId }) => {
            if (resolvedTyreId) {
              selectedTyreIdsByField.set(`${axleId}:${fieldKey}`, String(resolvedTyreId));
            }
          });
        });
        const attachmentAxlesPayload = displayedAxleConfigurations
          .map((axle, index) => {
            const axleRecordId = getPositiveNumber(axle?.id);
            if (!axleRecordId || !vehicleApiId) return null;

            const leftTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.left,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.left),
                initialValue: axle?.assignments?.left,
              }
            );
            const rightTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.right,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.right),
                initialValue: axle?.assignments?.right,
              }
            );
            const leftInnerTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.leftInner,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.leftInner),
                initialValue: axle?.assignments?.leftInner,
              }
            );
            const leftOuterTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.leftOuter,
              {
                fallbackId: getPositiveNumber(
                  axle?.assignments?.leftOuter,
                  axle?.assignments?.left
                ),
                initialValue: axle?.assignments?.leftOuter || axle?.assignments?.left,
              }
            );
            const rightInnerTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.rightInner,
              {
                fallbackId: getPositiveNumber(axle?.assignments?.rightInner),
                initialValue: axle?.assignments?.rightInner,
              }
            );
            const rightOuterTyreId = resolveSelectOptionId(
              tyreOptions,
              axle?.assignments?.rightOuter,
              {
                fallbackId: getPositiveNumber(
                  axle?.assignments?.rightOuter,
                  axle?.assignments?.right
                ),
                initialValue: axle?.assignments?.rightOuter || axle?.assignments?.right,
              }
            );
            const isDoubleTyreConfiguration = axle?.tyreConfiguration === "dual";

            return {
              id: String(axleRecordId),
              outer_left_tyre_id: isDoubleTyreConfiguration
                ? (leftOuterTyreId ? String(leftOuterTyreId) : null)
                : (leftTyreId ? String(leftTyreId) : null),
              outer_right_tyre_id: isDoubleTyreConfiguration
                ? (rightOuterTyreId ? String(rightOuterTyreId) : null)
                : (rightTyreId ? String(rightTyreId) : null),
              inner_left_tyre_id: isDoubleTyreConfiguration
                ? (leftInnerTyreId ? String(leftInnerTyreId) : null)
                : null,
              inner_right_tyre_id: isDoubleTyreConfiguration
                ? (rightInnerTyreId ? String(rightInnerTyreId) : null)
                : null,
              source_id: vehicleApiId,
              source_type: "vehicle",
              type: isDoubleTyreConfiguration ? "double" : "single",
            };
          })
          .filter(Boolean);
        const vehicleTyreOdmValue = normalizeNumber(
          vehicle?.currentOdometerValue ??
          vehicleSingleDetailData?.current_info?.odm ??
          vehicleSingleDetailData?.odm,
          0
        );
        let tyreLinkValidationMessage = "";
        if (
          !vehicleApiId ||
          !tyreLinkSelections.length ||
          attachmentAxlesPayload.length !== tyreLinkSelections.length
        ) {
          tyreLinkValidationMessage = "Vehicle axle data is missing";
        } else if (!linkedVehicleTyresSchema.isValidSync(tyreLinkSelections)) {
          tyreLinkValidationMessage = "Please select tyres for all axle positions";
        }
        const canLinkTyres = canUpdate && !tyreLinkValidationMessage;
        const currentLinkedEquipmentApiId = getPositiveNumber(
          currentLinkedEquipmentRecord?.equipment_id,
          currentLinkedEquipmentOption?.optionId,
          currentLinkedEquipmentOption?.id
        );
        const isEquipmentLinkMode =
          !currentLinkedEquipmentApiId || isEquipmentSelectionUnlocked;
        const linkedEquipmentDisplayLabel =
          linkedEquipmentValue?.label ||
          linkedEquipmentValue?.value ||
          (currentLinkedEquipmentApiId ? `EQ-${currentLinkedEquipmentApiId}` : "");

        const handleEquipmentLinkAction = async () => {
          if (!selectedEquipmentApiId || !vehicleApiId) {
            errorNotify(
              isEquipmentLinkMode
                ? "Please select equipment to link"
                : "Linked equipment is missing"
            );
            return;
          }

          try {
            const equipmentPayload = {
              vehicle_id: String(vehicleApiId),
              equipment_id: String(selectedEquipmentApiId),
              history_id: String(currentLinkedEquipmentRecord?.id || ""),
            };

            await dispatch(
              isEquipmentLinkMode
                ? attachVehicleEquipment(equipmentPayload)
                : detachVehicleEquipment(equipmentPayload)
            ).unwrap();

            await dispatch(fetchVehicleAssignmentHistory(vehicleApiId)).unwrap();
            if (isEquipmentLinkMode) {
              setIsEquipmentSelectionUnlocked(false);
              successNotify("Equipment linked successfully");
            } else {
              setLinkedEquipmentDraft("");
              setFieldValue("linkedEquipmentId", "");
              setIsEquipmentSelectionUnlocked(true);
              successNotify("Equipment unlinked successfully");
            }
          } catch (error) {
            errorNotify(
              error ||
              (isEquipmentLinkMode
                ? "Failed to link equipment"
                : "Failed to unlink equipment")
            );
          }
        };

        const statusSelectStyles = {
          control: (baseStyles) => ({
            ...baseStyles,
            minHeight: 20,
            border: "none",
            borderColor: "transparent",
            boxShadow: "none",
            background: "transparent",
            "&:hover": {
              border: "none",
              borderColor: "transparent",
            },
          }),
          valueContainer: (baseStyles) => ({
            ...baseStyles,
            padding: 0,
            paddingLeft: 0,
          }),
          singleValue: (baseStyles) => ({
            ...baseStyles,
            color: currentStatusMeta.color,
            fontSize: "14px",
            fontWeight: 600,
          }),
          placeholder: (baseStyles) => ({
            ...baseStyles,
            color: "#3d3f5c",
            fontSize: "14px",
          }),
          dropdownIndicator: (baseStyles) => ({
            ...baseStyles,
            color: "#3d3f5c",
            padding: 0,
          }),
          menu: (baseStyles) => ({
            ...baseStyles,
            left: "-28px",
            minWidth: "calc(100% + 28px)",
            width: "max-content",
          }),
          option: (baseStyles) => ({
            ...baseStyles,
            padding: "10px 12px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }),
        };
        const vehicleStatusTopOptions = vehicleStatusOptions.map((option) => ({
          ...option,
          icon: statusMeta(option.value).icon,
        }));


        const syncAxleCount = (nextValue) => {
          const rawValue = String(nextValue ?? "").trim();
          if (!rawValue) {
            setFieldValue("numberOfAxles", "");
            return;
          }

          const nextCount = Math.max(1, Math.trunc(normalizeNumber(rawValue, 1)));
          setFieldValue("numberOfAxles", String(nextCount));
          setFieldValue(
            "axleConfigurations",
            buildAxleConfigurations(values.axleConfigurations, nextCount)
          );
        };

        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function" ? updater(values.attachments || []) : updater;
          setFieldValue("attachments", nextFiles);
          setFieldValue("files", nextFiles);
          setFieldValue("driverDocument", nextFiles);
          setFieldTouched("attachments", true, true);
        };

        const getSwapAssetOptions = (assetType) => {
          if (assetType === "vehicle") return vehicleSwapAssetOptions;
          if (assetType === "equipment") return equipmentSwapAssetOptions;
          return [];
        };

        const sourceAssetLabel =
          swapTyreForm.sourceType === "equipment"
            ? (
              equipmentOptions.find(
                (option) =>
                  getPositiveNumber(option?.optionId, option?.id, option?.value) ===
                  getPositiveNumber(swapTyreForm.sourceAssetId)
              )?.label ||
              currentLinkedEquipmentOption?.label ||
              normalizeLinkedValue(vehicle?.linkedEquipmentId) ||
              (swapTyreForm.sourceAssetId
                ? `Equipment ${swapTyreForm.sourceAssetId}`
                : "")
            )
            : values.systemId ||
            vehicle?.systemId ||
            (swapTyreForm.sourceAssetId ? `Vehicle ${swapTyreForm.sourceAssetId}` : "");
        const sourceAssetOptions = swapTyreForm.sourceAssetId
          ? [
            {
              label: sourceAssetLabel || String(swapTyreForm.sourceAssetId),
              value: String(swapTyreForm.sourceAssetId),
              optionId: getPositiveNumber(swapTyreForm.sourceAssetId),
            },
          ]
          : getSwapAssetOptions(swapTyreForm.sourceType);
        const targetAssetOptions = getSwapAssetOptions(swapTyreForm.targetType);
        const sourcePositionOptions = sourceAssetTyreOptions;
        const targetPositionOptions = targetAssetTyreOptions;
        const selectedSourcePositionOption = findOption(
          sourcePositionOptions,
          swapTyreForm.sourcePosition
        );
        const selectedTargetPositionOption = findOption(
          targetPositionOptions,
          swapTyreForm.targetPosition
        );

        const resolveSwapAssetOdm = (assetType, assetId) => {
          const normalizedAssetType = normalizeSwapAssetType(assetType);
          const assetOptions =
            normalizedAssetType === "vehicle" ? vehicleSwapAssetOptions : equipmentSwapAssetOptions;
          const matchedOption = findOption(assetOptions, assetId);

          return normalizeNumber(
            matchedOption?.odm ??
            matchedOption?.current_info?.odm ??
            matchedOption?.vehicle?.odm ??
            matchedOption?.currentOdometerValue ??
            matchedOption?.current_odometer ??
            vehicleTyreOdmValue,
            vehicleTyreOdmValue
          );
        };

        const buildSwapTyrePayloadSection = (sectionKey, selectedOption) => {
          const assetType = normalizeSwapAssetType(swapTyreForm[`${sectionKey}Type`]);
          const assetId = getPositiveNumber(swapTyreForm[`${sectionKey}AssetId`]);

          return {
            position_identifier: String(selectedOption?.positionIdentifier || "").trim(),
            installed_history_id: getPositiveNumber(
              selectedOption?.assignmentId,
              selectedOption?.optionId,
              selectedOption?.id
            ),
            tyre_id: getPositiveNumber(selectedOption?.tyreId),
            axle_id: getPositiveNumber(selectedOption?.axleId),
            odm: resolveSwapAssetOdm(assetType, assetId),
            asset_id: assetId,
            asset_type: assetType,
          };
        };

        const openSwapTyresModal = () => {
          if (!canUpdate) return;

          const sourceAssetId =
            preferredSwapSourceAssetId || String(getPositiveNumber(vehicleApiId) || "");

          setSwapTyreForm({
            sourceType: preferredSwapSourceType,
            sourceAssetId,
            sourcePosition: "",
            targetType: "",
            targetAssetId: "",
            targetPosition: "",
          });
          setShowSwapTyresModal(true);
        };

        const handleSwapTypeChange = (sectionKey, nextType) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}Type`]: nextType,
            [`${sectionKey}AssetId`]: "",
            [`${sectionKey}Position`]: "",
          }));
        };

        const handleSwapAssetChange = (sectionKey, assetId) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}AssetId`]: assetId,
            [`${sectionKey}Position`]: "",
          }));
        };

        const handleSwapPositionChange = (sectionKey, position) => {
          setSwapTyreForm((previous) => ({
            ...previous,
            [`${sectionKey}Position`]: position,
          }));
        };

        const handleSwapTyresUpdate = async () => {
          const fromPayload = buildSwapTyrePayloadSection("source", selectedSourcePositionOption);
          const toPayload = buildSwapTyrePayloadSection("target", selectedTargetPositionOption);

          const hasInvalidPayload = [fromPayload, toPayload].some(
            (section) =>
              !section?.position_identifier ||
              !section?.installed_history_id ||
              !section?.tyre_id ||
              !section?.axle_id ||
              !section?.asset_id ||
              !section?.asset_type
          );

          if (hasInvalidPayload) {
            errorNotify("Please select valid tyre positions before updating");
            return;
          }

          setSwapTyresLoading(true);

          try {
            await apiService.swipeTyres({
              from: fromPayload,
              to: toPayload,
            });

            if (vehicleApiId) {
              await dispatch(fetchVehicleAssignmentHistory(vehicleApiId)).unwrap();
            }
            if (detailVehicleId) {
              await dispatch(fetchVehicleSingleDetail(detailVehicleId)).unwrap();
            }

            setShowSwapTyresModal(false);
            successNotify("Tyres swapped successfully");
          } catch (error) {
            errorNotify(error || "Failed to swap tyres");
          } finally {
            setSwapTyresLoading(false);
          }
        };

        const handleAttachVehicleTyres = async () => {
          if (!vehicleApiId || !attachmentAxlesPayload.length) {
            errorNotify("Vehicle axle data is missing");
            return;
          }

          if (!canLinkTyres) {
            errorNotify(tyreLinkValidationMessage || "Please select tyres for all axle positions");
            return;
          }

          try {
            await dispatch(
              attachVehicleTyres({
                source_id: vehicleApiId,
                source_type: "vehicle",
                axles: attachmentAxlesPayload,
              })
            ).unwrap();

            await dispatch(fetchVehicleAssignmentHistory(vehicleApiId)).unwrap();
            successNotify("Vehicle tyres linked successfully");
          } catch (error) {
            errorNotify(error || "Failed to link vehicle tyres");
          }
        };

        const canSubmitSwapTyres =
          canUpdate &&
          Boolean(swapTyreForm.sourceAssetId) &&
          Boolean(swapTyreForm.sourcePosition) &&
          Boolean(swapTyreForm.targetAssetId) &&
          Boolean(swapTyreForm.targetPosition);
        const nextInstallmentNumber = String(
          installmentData.reduce(
            (maxValue, row) => Math.max(maxValue, normalizeNumber(row?.number, 0)),
            0
          ) + 1
        ).padStart(2, "0");
        const requiresBankTransferDetails =
          installmentForm.paymentMethod === "bank_transfer";
        const isInstallmentCreationDisabled = isFullPaymentType(values.paymentType);
        const canAddInstallment =
          canUpdate &&
          Boolean(installmentForm.paymentMethod) &&
          Boolean(String(installmentForm.amount || "").trim()) &&
          Boolean(installmentForm.submissionDate) &&
          (!requiresBankTransferDetails ||
            (Boolean(String(installmentForm.bankName || "").trim()) &&
              Boolean(String(installmentForm.accountTitle || "").trim()) &&
              Boolean(String(installmentForm.ibanNumber || "").trim())));

        const handleAddInstallment = async () => {
          if (isInstallmentCreationDisabled) return;

          const nextRow = {
            number: nextInstallmentNumber,
            paymentMethod: installmentForm.paymentMethod,
            amount: normalizeNumber(installmentForm.amount),
            submissionDate: installmentForm.submissionDate,
            dueDate: installmentForm.submissionDate,
            attachmentsCount: (installmentForm.files || []).length,
            attachments: installmentForm.files || [],
            status: "pending",
          };

          if (!canUseVehicleInstallmentsApi || !vehicleApiId) {
            setInstallmentData((previous) => [...previous, nextRow]);
            setShowAddInstallmentModal(false);
            setInstallmentForm(createInstallmentFormState());
            successNotify("Installment added successfully");
            return;
          }

          try {
            await dispatch(
              createVehicleInstallment({
                vehicle_id: vehicleApiId,
                order_number: parseInt(nextInstallmentNumber, 10) || 1,
                payment_method: installmentForm.paymentMethod,
                amount: normalizeNumber(installmentForm.amount).toFixed(2),
                submit_at: toUtcDateTimeString(installmentForm.submissionDate),
                notes: String(installmentForm.notes || "").trim(),
                account_title: installmentForm.accountTitle || "",
                bank_name: installmentForm.bankName || "",
                iban_number: installmentForm.ibanNumber || "",
                reference_number: installmentForm.referenceNumber || "",
                files: normalizeInstallmentFiles(installmentForm.files),
              })
            ).unwrap();

            await dispatch(fetchVehicleInstallments(vehicleApiId)).unwrap();

            setShowAddInstallmentModal(false);
            setInstallmentForm(createInstallmentFormState());
            successNotify("Installment added successfully");
          } catch (error) {
            errorNotify(error || "Failed to add installment");
          }
        };

        const fieldLabelMap = {
          left: (axleId) => `${axleId} Left`,
          right: (axleId) => `${axleId} Right`,
          leftInner: (axleId) => `${axleId} Left Inner`,
          leftOuter: (axleId) => `${axleId} Left Outer`,
          rightInner: (axleId) => `${axleId} Right Inner`,
          rightOuter: (axleId) => `${axleId} Right Outer`,
        };
        const fieldPositionIdentifierMap = {
          left: "outer_left_tyre_id",
          right: "outer_right_tyre_id",
          leftInner: "inner_left_tyre_id",
          leftOuter: "outer_left_tyre_id",
          rightInner: "inner_right_tyre_id",
          rightOuter: "outer_right_tyre_id",
        };

        const handleDetachVehicleTyre = async ({
          axle,
          axleFormIndex,
          fieldKey,
          fieldLabel,
        }) => {
          const positionIdentifier = String(
            fieldPositionIdentifierMap[fieldKey] || ""
          ).trim();
          const tyreId = resolveSelectOptionId(
            tyreOptions,
            axle?.assignments?.[fieldKey],
            {
              fallbackId: getPositiveNumber(axle?.assignments?.[fieldKey]),
              initialValue: axle?.assignments?.[fieldKey],
            }
          );
          const axleId = getPositiveNumber(axle?.id);
          const sourceId = getPositiveNumber(axle?.sourceId, axle?.source_id, vehicleApiId);
          const sourceType = String(axle?.sourceType || axle?.source_type || "vehicle").trim();
          const fieldStateKey = `${axle?.axleId || axleFormIndex}:${fieldKey}`;

          if (!positionIdentifier || !tyreId || !axleId || !sourceId || !sourceType) {
            errorNotify(`Unable to detach tyre from ${fieldLabel}`);
            return;
          }

          setDetachingTyreFieldKey(fieldStateKey);

          try {
            await dispatch(
              detachVehicleTyre({
                axle_id: axleId,
                tyre_id: tyreId,
                position_identifier: positionIdentifier,
                source_id: sourceId,
                source_type: sourceType,
              })
            ).unwrap();

            setFieldValue(
              `axleConfigurations[${axleFormIndex}].assignments.${fieldKey}`,
              ""
            );

            const refreshRequests = [dispatch(fetchTyresDropdown()).unwrap()];

            if (vehicleApiId) {
              refreshRequests.push(dispatch(fetchVehicleAssignmentHistory(vehicleApiId)).unwrap());
            }

            if (detailVehicleId) {
              refreshRequests.push(dispatch(fetchVehicleSingleDetail(detailVehicleId)).unwrap());
            }

            await Promise.all(refreshRequests);
            successNotify(`${fieldLabel} tyre detached successfully`);
          } catch (error) {
            errorNotify(error || `Failed to detach tyre from ${fieldLabel}`);
          } finally {
            setDetachingTyreFieldKey("");
          }
        };

        const renderAssignmentAxleSection = (axle, index) => {
          const axleId = axle?.axleId || `A${index + 1}`;
          const axleFormIndex = axleFormIndexById.get(String(axleId)) ?? index;
          const assignmentFields = getAxleAssignmentFields(axle?.tyreConfiguration);
          const getAvailableTyreOptions = (fieldKey) => {
            const currentFieldMapKey = `${axleId}:${fieldKey}`;
            const currentSelectedTyreId = selectedTyreIdsByField.get(currentFieldMapKey) || "";
            const blockedTyreIds = new Set(
              Array.from(selectedTyreIdsByField.entries())
                .filter(([mapKey]) => mapKey !== currentFieldMapKey)
                .map(([, tyreId]) => String(tyreId))
            );

            const filteredOptions = tyreOptions.filter((option) => {
              const optionId = getPositiveNumber(option?.optionId, option?.id, option?.value);
              if (!optionId) return true;
              if (currentSelectedTyreId && String(optionId) === currentSelectedTyreId) {
                return true;
              }
              return !blockedTyreIds.has(String(optionId));
            });

            return ensureCurrentOption(filteredOptions, axle?.assignments?.[fieldKey]);
          };

          return (
            <div className="assets_equipment_assignment_group" key={axleId}>
              <h5>Axle: {axleId}</h5>

              <div className="assets_equipment_assignment_configuration">
                <Select
                  label={renderFieldLabel("Tyre Configuration")}
                  placeholder="Select Tyre Configuration"
                  options={tyreConfigurationOptions}
                  value={findOption(tyreConfigurationOptions, axle?.tyreConfiguration)}
                  onChange={(option) =>
                    setFieldValue(
                      `axleConfigurations[${axleFormIndex}].tyreConfiguration`,
                      option?.value || "single"
                    )
                  }
                  onBlur={handleBlur}
                  isSearch={false}
                />
              </div>

              <Row className="assets_equipment_assignment_fields">
                {assignmentFields.map((field) => {
                  const availableTyreOptions = getAvailableTyreOptions(field.key);
                  const fieldLabel = fieldLabelMap[field.key](axleId);
                  const selectedTyreValue = axle?.assignments?.[field.key];
                  const selectedTyreId = resolveSelectOptionId(
                    availableTyreOptions,
                    selectedTyreValue,
                    {
                      fallbackId: getPositiveNumber(selectedTyreValue),
                      initialValue: selectedTyreValue,
                    }
                  );

                  const hasAssignedTyre = Boolean(getPositiveNumber(selectedTyreId));
                  const currentDetachKey = `${axleId}:${field.key}`;
                  const isDetachingCurrentTyre =
                    detachVehicleTyreLoading && detachingTyreFieldKey === currentDetachKey;
                  const isLinkedTyreFromAssignmentHistory =
                    linkedTyreAssignmentFields.has(currentDetachKey);

                  return (
                    <Col md={6} key={`${axleId}-${field.key}`}>
                      <div className="assets_vehicle_tyre_assignment_field">
                        <div className="assets_vehicle_tyre_assignment_select">
                          <Select
                            label={renderFieldLabel(fieldLabel)}
                            placeholder="Select Tyre ID"
                            options={availableTyreOptions}
                            loading={tyreDropdownLoading}
                            value={findOption(availableTyreOptions, selectedTyreValue)}
                            onChange={(option) =>
                              setFieldValue(
                                `axleConfigurations[${axleFormIndex}].assignments.${field.key}`,
                                option?.value || ""
                              )
                            }
                            onBlur={handleBlur}
                            isSearch={false}
                            isDisabled={isLinkedTyreFromAssignmentHistory}
                          />
                        </div>

                        <button
                          type="button"
                          className="assets_vehicle_tyre_detach_btn"
                          disabled={!canUpdate || !hasAssignedTyre || detachVehicleTyreLoading}
                          onClick={() =>
                            handleDetachVehicleTyre({
                              axle,
                              axleFormIndex,
                              fieldKey: field.key,
                              fieldLabel,
                            })
                          }
                        >
                          {isDetachingCurrentTyre ? "Detaching..." : "Detach"}
                        </button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </div>
          );
        };
        const canSaveActiveTab =
          activeTab === "general_info" || activeTab === "financial_insurance";
        const isSaveDetailsLoading = isSubmitting || updateVehiclePurchaseInfoLoading;
        const isSaveDetailsDisabled = isSaveDetailsLoading || !canSaveActiveTab;


        return (
          <div className="assets_vehicle_details_page">
            <Breadcrumbs values={["Assets", "Vehicles", "Vehicle Details"]} />

            <div className="page_header access_view">
              <h2>Vehicle Details</h2>
              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleSubmit}
                  submitText="Save Details"
                  type="button"
                  shouldRow={false}
                  isShowSubmitBtn={canUpdate}
                  loading={isSaveDetailsLoading}
                  disabled={isSaveDetailsDisabled}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={isHiredVehicle ? 12 : 8}>
                  <div className="assets_vehicle_header_strip">
                    <h6>
                      Vehicle ID: <span>{values.systemId}</span>
                    </h6>

                    {!isHiredVehicle && <div className="assets_vehicle_top_status">
                      <span>Status</span>
                      <Select
                        className="assets_vehicle_status_select"
                        label=""
                        placeholder="Select Status"
                        options={vehicleStatusTopOptions}
                        value={findOption(vehicleStatusTopOptions, values.status)}
                        onChange={(option) => setFieldValue("status", option?.value || "")}
                        onBlur={handleBlur}
                        isSearch={false}
                        required={false}
                        showIcon
                        icon={
                          <img
                            src={currentStatusMeta.icon}
                            alt={currentStatusMeta.label}
                            className="assets_vehicle_status_icon_inline"
                          />
                        }
                        customStyles={statusSelectStyles}
                      />
                    </div>}
                  </div>

                  <div className="assets_vehicle_tabs_wrap">
                    <CustomTabs tabs={detailTabOptions} activeTab={activeTab} setActiveTab={setActiveTab} />
                  </div>

                  {activeTab === "general_info" && (
                    isHiredVehicle ? (
                      <>
                        <div className="content_box assets_vehicle_content_box">
                          <h6>Hired / Contract Vehicle Information</h6>
                          <p>Main information for this vehicle record.</p>

                          <Row>
                            <Col md={12}>
                              <Select
                                label={renderFieldLabel("Transporter / Vendor")}
                                placeholder="Select Transporter"
                                options={threePlTransporterSelectOptions}
                                loading={dropdownLoading}
                                value={findOption(threePlTransporterSelectOptions, values.transporter)}
                                name="transporter"
                                onChange={(option) => {
                                  setFieldValue("transporter", option?.value || "");
                                  setFieldValue("transporterName", option?.label || "");
                                }}
                                onBlur={handleBlur}
                                error={errors.transporter}
                                touched={touched.transporter}
                                isSearch={false}
                              />
                            </Col>
                            <Col md={12}>
                              <Input
                                label={renderFieldLabel("Registration No.")}
                                placeholder="e.g. TKA-123"
                                name="registrationNumber"
                                value={values.registrationNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.registrationNumber}
                                touched={touched.registrationNumber}
                                required={false}
                              />
                            </Col>
                            <Col md={12}>
                              <Select
                                label={renderFieldLabel("Vehicle Type")}
                                placeholder="Select Type"
                                options={threePlVehicleTypeSelectOptions}
                                loading={dropdownLoading}
                                value={findOption(threePlVehicleTypeSelectOptions, values.vehicleType)}
                                name="vehicleType"
                                onChange={(option) => setFieldValue("vehicleType", option?.value || "")}
                                onBlur={handleBlur}
                                error={errors.vehicleType}
                                touched={touched.vehicleType}
                                isSearch={false}
                              />
                            </Col>
                            <Col md={4}>
                              <Input
                                label={renderFieldLabel("Driver Name")}
                                placeholder="e.g. Muhammad Ali"
                                name="driverName"
                                value={values.driverName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={errors.driverName}
                                touched={touched.driverName}
                                required={false}
                              />
                            </Col>
                            <Col md={4}>
                              <PhoneNumberInput
                                label={renderFieldLabel("Mobile No.")}
                                placeholder="923XXXXXXXXX"
                                name="mobileNumber"
                                value={values.mobileNumber}
                                onBlur={handleBlur}
                                error={errors.mobileNumber}
                                touched={touched.mobileNumber}
                                setFieldValue={setFieldValue}
                              />
                            </Col>
                            <Col md={4}>
                              <CnicInput
                                label={renderFieldLabel("CNIC")}
                                name="cnic"
                                value={values.cnic}
                                onBlur={handleBlur}
                                error={errors.cnic}
                                touched={touched.cnic}
                                setFieldValue={setFieldValue}
                              />
                            </Col>
                          </Row>
                        </div>

                        <div className="content_box assets_vehicle_content_box assets_vehicle_documents_section">
                          <h6>
                            Documents <span className="required_star">*</span>
                          </h6>
                          <div className="issue_file_upload_wrapper">
                            <label>
                              Driver CNIC / License
                              <span className="required_star">*</span>
                            </label>
                            <p style={{ margin: "4px 0 10px 0", fontSize: "13px", color: "#64748b" }}>
                              Upload relevant documents for this vehicle.
                            </p>
                            <FileUpload
                              destination="3pl-vehicles"
                              files={values.attachments || []}
                              setFiles={updateAttachments}
                              maxFileSize={5}
                              maxFiles={5}
                              accept="image/*,.pdf"
                              className="issue_file_upload create_vehicle_upload assets_vehicle_documents_upload"
                            />
                            {touched.attachments && errors.attachments && (
                              <div className="threepl_vehicle_error">
                                {errors.attachments}
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    ) : (
                    <>
                      <div className="content_box assets_vehicle_content_box">
                        <h6>Core Details</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Registration No.")}
                              placeholder="Enter Registration No."
                              name="registrationNumber"
                              value={values.registrationNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.registrationNumber}
                              touched={touched.registrationNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("VIN / Chassis No.")}
                              placeholder="Enter VIN / Chassis No."
                              name="vinChassisNo"
                              value={values.vinChassisNo}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.vinChassisNo}
                              touched={touched.vinChassisNo}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Make")}
                              placeholder="Select Make"
                              options={makeSelectOptions}
                              value={findOption(makeSelectOptions, values.make)}
                              onChange={(option) => setFieldValue("make", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.make}
                              touched={touched.make}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Model")}
                              placeholder="Select Model"
                              options={modelSelectOptions}
                              value={findOption(modelSelectOptions, values.model)}
                              onChange={(option) => setFieldValue("model", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.model}
                              touched={touched.model}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Vehicle Type", false)}
                              placeholder="Vehicle Type"
                              name="vehicleType"
                              value={values.vehicleType || "-"}
                              disabled
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Year")}
                              type="number"
                              placeholder="YYYY"
                              name="year"
                              value={values.year}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.year}
                              touched={touched.year}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Fuel Type")}
                              placeholder="Select Fuel Type"
                              options={fuelTypeOptions}
                              value={
                                fuelTypeOptions.find((option) => option.value === values.fuelType) ||
                                null
                              }
                              onChange={(option) => setFieldValue("fuelType", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.fuelType}
                              touched={touched.fuelType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Tank Capacity")}
                              type="number"
                              placeholder="00"
                              name="tankCapacity"
                              value={values.tankCapacity}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.tankCapacity}
                              touched={touched.tankCapacity}
                              showExtraText
                              extraText="Litres"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Current Fuel Efficiency")}
                              type="number"
                              placeholder="00"
                              name="currentFuelEfficiency"
                              value={values.currentFuelEfficiency}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.currentFuelEfficiency}
                              touched={touched.currentFuelEfficiency}
                              showExtraText
                              extraText="km/l"
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Transmission Type")}
                              placeholder="Select Transmission Type"
                              options={transmissionSelectOptions}
                              value={findOption(transmissionSelectOptions, values.transmissionType)}
                              onChange={(option) =>
                                setFieldValue("transmissionType", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.transmissionType}
                              touched={touched.transmissionType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Engine Capacity")}
                              placeholder="Enter Engine Capacity"
                              type="number"
                              name="engineCapacity"
                              value={values.engineCapacity}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.engineCapacity}
                              touched={touched.engineCapacity}
                              required={false}
                            />
                          </Col>

                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Branch")}
                              placeholder="Select Branch"
                              options={branchSelectOptions}
                              value={findOption(branchSelectOptions, values.branch)}
                              onChange={(option) => setFieldValue("branch", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.branch}
                              touched={touched.branch}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Status")}
                              placeholder="Select Status"
                              options={vehicleStatusOptions}
                              value={findOption(vehicleStatusOptions, values.status)}
                              onChange={(option) => setFieldValue("status", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.status}
                              touched={touched.status}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Number of Axles")}
                              placeholder="00"
                              type="number"
                              name="numberOfAxles"
                              value={values.numberOfAxles}
                              onChange={(event) => syncAxleCount(event.target.value)}
                              onBlur={handleBlur}
                              error={errors.numberOfAxles}
                              touched={touched.numberOfAxles}
                              // disabled
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("IMEI Tracker Code", false)}
                              placeholder="00"
                              type="number"
                              name="imeino"
                              value={values.imeino}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.imeino}
                              touched={touched.imeino}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel(
                                "Tracking Package",
                                hasImeiTrackerValue(values.imeino)
                              )}
                              placeholder="Select Tracking Package"
                              options={trackingPackageOptions}
                              value={findOption(trackingPackageOptions, values.trackingPackage)}
                              onChange={(option) =>
                                setFieldValue("trackingPackage", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.trackingPackage}
                              touched={touched.trackingPackage}
                              isSearch={false}
                              isClearable
                              required={hasImeiTrackerValue(values.imeino)}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Primary Driver")}
                              placeholder="Select Driver"
                              options={driverOptions}
                              value={findOption(driverOptions, values.primaryDriver)}
                              onChange={(option) =>
                                setFieldValue("primaryDriver", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.primaryDriver}
                              touched={touched.primaryDriver}
                              loading={driverDropdownLoading}
                              isSearch
                              isOptionDisabled={(option) => Boolean(option?.isDisabled)}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Secondary Driver", false)}
                              placeholder="Select Driver"
                              options={driverOptions}
                              value={findOption(driverOptions, values.secondaryDriver)}
                              onChange={(option) =>
                                setFieldValue("secondaryDriver", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.secondaryDriver}
                              touched={touched.secondaryDriver}
                              loading={driverDropdownLoading}
                              isSearch
                              isClearable
                              required={false}
                              isOptionDisabled={(option) => Boolean(option?.isDisabled)}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Helper", false)}
                              placeholder="Select Helper"
                              options={helperOptions}
                              value={findOption(helperOptions, values.helper)}
                              onChange={(option) =>
                                setFieldValue("helper", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.helper}
                              touched={touched.helper}
                              loading={helperDropdownLoading}
                              isSearch
                              isClearable
                              required={false}
                              isOptionDisabled={(option) => Boolean(option?.isDisabled)}
                            />
                          </Col>
                          <Col md={12} style={{ padding: 0 }}>
                            <Input
                              label={renderFieldLabel("Description", false)}
                              placeholder="Write detail of part if any..."
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.description}
                              touched={touched.description}
                              quill
                              required={false}
                              setFieldValue={setFieldValue}
                              className="create_vehicle_description"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_vehicle_documents_section">
                        <h6>Photos &amp; Documents</h6>
                        <p>Upload relevant photos or documents for this vehicle.</p>
                        <FileUpload
                          destination="vehicle-registration"
                          files={values.attachments || []}
                          setFiles={updateAttachments}
                          maxFileSize={10}
                          maxFiles={5}
                          className="create_vehicle_upload assets_vehicle_documents_upload"
                        />
                      </div>

                    </>
                    )
                  )}

                  {activeTab === "service_records" && (
                    <div className="content_box assets_vehicle_content_box">
                      <h6>Vehicle Records</h6>
                      <p>Main information for this part record.</p>

                      <NewTableComponent
                        showAddBtn={false}
                        showFilter={false}
                        columns={serviceColumns}
                        data={paginatedServiceRows}
                        currentPage={servicePage}
                        setCurrentPage={setServicePage}
                        limit={serviceLimit}
                        setLimit={(val) => {
                          setServicePage(1);
                          setServiceLimit(val);
                        }}
                        totalItems={serviceTotalItems}
                        totalPages={serviceTotalPages}
                        showPagination
                        paginationLoading={canUseVehicleServicesApi && vehicleServiceRecordsLoading}
                        loading={canUseVehicleServicesApi && vehicleServiceRecordsLoading}
                        searchFilters={{}}
                        setSearchFilters={() => { }}
                        tableClassName="assets_vehicle_inner_table"
                        limitOptions={[10, 20, 50, 100]}
                        paginationClassName="inline_pagination"
                      />
                    </div>
                  )}
                  {activeTab === "assignment_history" && (
                    <>
                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <h6>Equipment Record</h6>
                        <p>Main information for this part record.</p>

                        <div className="assets_vehicle_assignment_link_row">
                          <div className="assets_vehicle_assignment_link_field">
                            {isEquipmentLinkMode ? (
                              <AssetLinkField
                                label="Current Linked Equipment"
                                placeholder="Select Equipment"

                                options={(equipmentOptions.map(item => ({
                                  ...item,
                                  isDisabled: item?.status === "active" ? false : true,
                                }))
                                )}
                                loading={equipmentDropdownLoading}
                                value={linkedEquipmentValue}
                                onChange={(option) => setLinkedEquipmentDraft(option?.value || "")}
                                onBlur={() => { }}
                                isSearch
                                isDisabled={!isEquipmentSelectionUnlocked || attachVehicleEquipmentLoading}
                              />
                            ) : (
                              <div className="assets_vehicle_assignment_link_display">
                                <span
                                  className="service-task"
                                  title={linkedEquipmentDisplayLabel}
                                >
                                  {linkedEquipmentDisplayLabel}
                                </span>
                              </div>
                            )}
                          </div>

                          <button
                            type="button"
                            className="assets_vehicle_assignment_unlink"
                            onClick={handleEquipmentLinkAction}
                            disabled={
                              !canUpdate ||
                              attachVehicleEquipmentLoading ||
                              (isEquipmentLinkMode && !selectedEquipmentApiId)
                            }
                          >
                            {linkedIcon}
                            <span>
                              {attachVehicleEquipmentLoading
                                ? isEquipmentLinkMode
                                  ? "Linking..."
                                  : "Unlinking..."
                                : isEquipmentLinkMode
                                  ? "Link"
                                  : "Unlink"}
                            </span>
                          </button>
                        </div>

                        <h5 className="assets_equipment_assignment_history_title">
                          Equipment Assignment History
                        </h5>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={equipmentColumns}
                          data={assignmentEquipmentRows}
                          currentPage={equipmentPage}
                          setCurrentPage={setEquipmentPage}
                          limit={equipmentLimit}
                          setLimit={(val) => {
                            setEquipmentPage(1);
                            setEquipmentLimit(val);
                          }}
                          totalItems={assignmentEquipmentHistoryData.length}
                          totalPages={Math.max(1, Math.ceil(assignmentEquipmentHistoryData.length / equipmentLimit))}
                          showPagination={shouldShowAssignmentPagination}
                          paginationLoading={canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryLoading}
                          loading={canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table assets_equipment_assignment_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>

                      <div className="content_box assets_vehicle_content_box assets_equipment_content_box">
                        <div className="assets_equipment_assignment_header">
                          <div>
                            <h6>Linked Tyres &amp; Positions</h6>
                            <p>Main information for this part record.</p>
                          </div>

                          <button
                            type="button"
                            className="assets_equipment_swap_btn"
                            onClick={openSwapTyresModal}
                            disabled={!canUpdate}
                          >
                            <FiRefreshCw />
                            Swap Tyres
                          </button>
                        </div>

                        <div className="assets_equipment_assignment_wrap">
                          {displayedAxleConfigurations.map((axle, index) =>
                            renderAssignmentAxleSection(axle, index)
                          )}
                        </div>

                        <div className="assets_vehicle_link_tyres_actions">
                          <button
                            type="button"
                            className="main_btn assets_vehicle_link_tyres_btn"
                            onClick={handleAttachVehicleTyres}
                            disabled={!canLinkTyres || attachVehicleTyresLoading}
                          >
                            {attachVehicleTyresLoading ? "Linking..." : "Link Tyre"}
                          </button>
                        </div>

                        <h5 className="assets_equipment_assignment_history_title">
                          Tyres Installed History
                        </h5>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={tyreColumns}
                          data={paginatedAssignmentTyreRows}
                          currentPage={tyrePage}
                          setCurrentPage={setTyrePage}
                          limit={tyreLimit}
                          setLimit={(val) => {
                            setTyrePage(1);
                            setTyreLimit(val);
                          }}
                          totalItems={tyreHistoryData.length}
                          totalPages={Math.max(1, Math.ceil(tyreHistoryData.length / tyreLimit))}
                          showPagination={shouldShowTyrePagination}
                          paginationLoading={canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryLoading}
                          loading={canUseVehicleAssignmentHistoryApi && vehicleAssignmentHistoryLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table assets_equipment_assignment_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>
                    </>
                  )}

                  {activeTab === "financial_insurance" && (
                    <>
                      <div className="content_box assets_vehicle_content_box">
                        <h6>Purchase Summary</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Purchase Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="purchaseDate"
                              value={values.purchaseDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.purchaseDate}
                              touched={touched.purchaseDate}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Purchase Cost")}
                              type="number"
                              placeholder="00"
                              name="purchaseCost"
                              value={values.purchaseCost}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.purchaseCost}
                              touched={touched.purchaseCost}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Payment Type")}
                              placeholder="Select Payment Type"
                              name="paymentType"
                              options={paymentTypeOptions}
                              value={findOption(paymentTypeOptions, values.paymentType)}
                              isDisabled={isInstallmentCreationDisabled}
                              onChange={(option) => {
                                const nextPaymentType = option?.value || "";
                                setFieldValue("paymentType", nextPaymentType);

                                if (isFullPaymentType(nextPaymentType)) {
                                  setShowAddInstallmentModal(false);
                                  setInstallmentForm(createInstallmentFormState());
                                }
                              }}
                              onBlur={handleBlur}
                              error={errors.paymentType}
                              touched={touched.paymentType}
                              required={false}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Depreciation Method")}
                              placeholder="Select Depreciation Method"
                              name="depreciationMethod"
                              options={depreciationMethodOptions}
                              value={findOption(
                                depreciationMethodOptions,
                                values.depreciationMethod
                              )}
                              onChange={(option) => setFieldValue("depreciationMethod", option?.value || "")}
                              onBlur={handleBlur}
                              error={errors.depreciationMethod}
                              touched={touched.depreciationMethod}
                              required={false}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Useful Life")}
                              type="number"
                              placeholder="Enter Useful Life"
                              name="usefulLife"
                              value={values.usefulLife}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.usefulLife}
                              touched={touched.usefulLife}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Residual Value")}
                              type="number"
                              placeholder="00"
                              name="residualValue"
                              value={values.residualValue}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.residualValue}
                              touched={touched.residualValue}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                        </Row>
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Installment Record</h6>
                        <p>Main information for this part record.</p>

                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={installmentColumns}
                          data={paginatedInstallmentRows}
                          currentPage={installmentPage}
                          setCurrentPage={setInstallmentPage}
                          limit={installmentLimit}
                          setLimit={(val) => {
                            setInstallmentPage(1);
                            setInstallmentLimit(val);
                          }}
                          totalItems={installmentData.length}
                          totalPages={Math.max(1, Math.ceil(installmentData.length / installmentLimit))}
                          showPagination={false}
                          paginationLoading={false}
                          loading={canUseVehicleInstallmentsApi && vehicleInstallmentsLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                        <button
                          type="button"
                          className="asset_vehicle_link_row_btn"
                          onClick={() => {
                            if (isInstallmentCreationDisabled) return;
                            setShowAddInstallmentModal(true);
                          }}
                          disabled={isInstallmentCreationDisabled}
                        >
                          <FiPlus />
                          Add Installment
                        </button>
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Insurance History</h6>
                        <p>Main information for this part record.</p>

                        <Row>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Policy Number")}
                              placeholder="Enter Policy Number"
                              name="policyNumber"
                              value={values.policyNumber}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.policyNumber}
                              touched={touched.policyNumber}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Provider")}
                              placeholder="Select Provider"
                              name="insuranceProvider"
                              options={insuranceProviderSelectOptions}
                              value={findOption(
                                insuranceProviderSelectOptions,
                                values.insuranceProvider
                              )}
                              onChange={(option) =>
                                setFieldValue("insuranceProvider", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.insuranceProvider}
                              touched={touched.insuranceProvider}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Select
                              label={renderFieldLabel("Coverage Type")}
                              placeholder="Select Coverage Type"
                              name="coverageType"
                              options={coverageTypeOptions}
                              value={findOption(coverageTypeOptions, values.coverageType)}
                              onChange={(option) =>
                                setFieldValue("coverageType", option?.value || "")
                              }
                              onBlur={handleBlur}
                              error={errors.coverageType}
                              touched={touched.coverageType}
                              isSearch={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Coverage Amount")}
                              placeholder="00"
                              name="coverageAmount"
                              type="number"
                              value={values.coverageAmount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.coverageAmount}
                              touched={touched.coverageAmount}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Premium")}
                              placeholder="00"
                              name="premiumAmount"
                              type="number"
                              value={values.premiumAmount}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.premiumAmount}
                              touched={touched.premiumAmount}
                              required={false}
                              showExtraText
                              extraText="PKR"
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Start Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="insuranceStartDate"
                              value={values.insuranceStartDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.insuranceStartDate}
                              touched={touched.insuranceStartDate}
                              required={false}
                            />
                          </Col>
                          <Col md={4}>
                            <Input
                              label={renderFieldLabel("Expiry Date")}
                              type="date"
                              placeholder="DD/MM/YYYY"
                              name="insuranceExpiryDate"
                              value={values.insuranceExpiryDate}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={errors.insuranceExpiryDate}
                              touched={touched.insuranceExpiryDate}
                              required={false}
                            />
                          </Col>
                        </Row>
                      </div>
                    </>
                  )}
                  {activeTab === "operational_data" && (
                    <>
                      <div className="content_box assets_vehicle_content_box">
                        <h6>Trip History</h6>
                        <p>Main information for this part record.</p>
                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={tripColumns}
                          data={paginatedTripRows}
                          currentPage={tripPage}
                          setCurrentPage={setTripPage}
                          limit={tripLimit}
                          setLimit={(val) => {
                            setTripPage(1);
                            setTripLimit(val);
                          }}
                          totalItems={tripTotalItems}
                          totalPages={tripTotalPages}
                          showPagination
                          paginationLoading={canUseVehicleTripsApi && vehicleTripsLoading}
                          loading={canUseVehicleTripsApi && vehicleTripsLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Fuel Logs</h6>
                        <p>Main information for this part record.</p>
                        <NewTableComponent
                          showAddBtn={false}
                          showFilter={false}
                          columns={fuelColumns}
                          data={paginatedFuelRows}
                          currentPage={fuelPage}
                          setCurrentPage={setFuelPage}
                          limit={fuelLimit}
                          setLimit={(val) => {
                            setFuelPage(1);
                            setFuelLimit(val);
                          }}
                          totalItems={fuelTotalItems}
                          totalPages={fuelTotalPages}
                          showPagination
                          paginationLoading={canUseVehicleFuelLogsApi && vehicleFuelLogsLoading}
                          loading={canUseVehicleFuelLogsApi && vehicleFuelLogsLoading}
                          searchFilters={{}}
                          setSearchFilters={() => { }}
                          tableClassName="assets_vehicle_inner_table"
                          limitOptions={[10, 20, 50, 100]}
                          paginationClassName="inline_pagination"
                        />
                      </div>

                      <div className="content_box assets_vehicle_content_box">
                        <h6>Distance Summary</h6>
                        <p>Main information for this part record.</p>
                        <div className="assets_vehicle_distance_cards">
                          <div className="assets_vehicle_distance_card">
                            <span>Total KM</span>
                            <strong>
                              {canUseVehicleDistanceSummaryApi && vehicleDistanceSummaryLoading
                                ? "Loading..."
                                : formatAmount(operationalSummary.totalKm)}
                            </strong>
                          </div>
                          <div className="assets_vehicle_distance_card">
                            <span>Average KM per Month</span>
                            <strong>
                              {canUseVehicleDistanceSummaryApi && vehicleDistanceSummaryLoading
                                ? "Loading..."
                                : formatAmount(operationalSummary.averageKmPerMonth)}
                            </strong>
                          </div>
                          <div className="assets_vehicle_distance_card">
                            <span>Last 30 Days KM</span>
                            <strong>
                              {canUseVehicleDistanceSummaryApi && vehicleDistanceSummaryLoading
                                ? "Loading..."
                                : formatAmount(operationalSummary.last30DaysKm)}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </Col>

                {!isHiredVehicle && <Col md={4}>
                  <div className="service_cost_summary_details assets_vehicle_right_sidebar">
                    <div className="service_cost_summary mb-3">
                      <h6>Cost Summary</h6>
                      <p>Financial Breakdown of the Service Entries (PKR)</p>

                      {showCostSummary ? (
                        <>
                          <div className="service_item"><div>Purchase Cost</div><div>{formatAmount(purchaseCost)}</div></div>
                          <div className="service_item"><div>Insurance Cost (Annual)</div><div>{formatAmount(insuranceAnnualCost)}</div></div>
                          <div className="service_item"><div>Maintenance Cost (Annual)</div><div>{formatAmount(maintenanceAnnualCost)}</div></div>
                          <div className="service_item"><div>Fuel Cost (Annual)</div><div>{formatAmount(fuelAnnualCost)}</div></div>
                          <div className="total"><div>Total</div><span>{formatAmount(totalRunningCost)}</span></div>
                          <div className="assets_vehicle_avg_cost">Avg Cost / km <span>{formatAmount(avgCost)} PKR</span></div>
                        </>
                      ) : (
                        <div className="assets_vehicle_empty_state"><p>No cost records available to generate summary</p></div>
                      )}
                    </div>

                    <LogsComments
                      {...(canUseVehicleHistoryApi
                        ? {
                          historyItems: Array.isArray(vehicleHistoryData)
                            ? vehicleHistoryData
                            : [],
                          onFetchHistory: handleFetchVehicleHistory,
                          onSubmitComment: handleSubmitVehicleComment,
                          loading: vehicleHistoryLoading,
                        }
                        : {
                          staticHistoryItems: assetHistoryItems,
                          commentValue: commentInput,
                          onCommentChange: setCommentInput,
                          onSendComment: handleCommentSend,
                        })}
                      editorModules={modules}
                      editorFormats={formats}
                      emptyMessages={{
                        all: "No comments or user activity found",
                        comment: "No comments found",
                        logs: "No user activity found",
                      }}
                    />

                    <div className="service_cost_summary_details rfq_right_card">
                      <div className="parts_general_notes">
                        <h5>General Notes</h5>
                        <p>Add any additional notes or observations here.</p>
                        <ReactQuill
                          placeholder={"write notes here..."}
                          modules={modules}
                          theme="snow"
                          value={values.notes || ""}
                          onChange={(val) => setFieldValue("notes", val)}
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </div>
                  </div>
                </Col>}
              </Row>
            </div>

            <CustomModal
              show={attachmentViewer.open}
              handleClose={closeAttachmentViewer}
              heading="Installment Attachments"
              size="lg"
              dialogClassName="custom_add_installment_modal"
            >
              <div className="assets_equipment_swap_modal_body custom_add_installment_modal_body">
                <div
                  style={{
                    position: "relative",
                    minHeight: "420px",
                    border: "1px solid #EAECF0",
                    borderRadius: "12px",
                    background: "#F8FAFC",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {hasMultipleAttachments ? (
                    <>
                      <button
                        type="button"
                        onClick={() => changeAttachmentSlide(-1)}
                        aria-label="Previous attachment"
                        style={{
                          position: "absolute",
                          left: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "1px solid #EAECF0",
                          background: "#FFFFFF",
                          color: "#25273D",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 10px 24px rgba(16, 24, 40, 0.16)",
                          zIndex: 2,
                        }}
                      >
                        <FiChevronLeft size={22} />
                      </button>
                      <button
                        type="button"
                        onClick={() => changeAttachmentSlide(1)}
                        aria-label="Next attachment"
                        style={{
                          position: "absolute",
                          right: "14px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          border: "1px solid #EAECF0",
                          background: "#FFFFFF",
                          color: "#25273D",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          boxShadow: "0 10px 24px rgba(16, 24, 40, 0.16)",
                          zIndex: 2,
                        }}
                      >
                        <FiChevronRight size={22} />
                      </button>
                    </>
                  ) : null}
                  <span
                    style={{
                      position: "absolute",
                      right: "14px",
                      bottom: "12px",
                      padding: "5px 10px",
                      borderRadius: "999px",
                      background: "rgba(37, 39, 61, 0.72)",
                      color: "#FFFFFF",
                      fontSize: "12px",
                      zIndex: 2,
                    }}
                  >
                    {attachmentViewer.currentIndex + 1} / {attachmentViewer.urls.length}
                  </span>
                  {isImageAttachment(currentAttachmentUrl) ? (
                    <img
                      src={currentAttachmentUrl}
                      alt={currentAttachmentName}
                      style={{
                        maxWidth: "100%",
                        maxHeight: "420px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <iframe
                      src={currentAttachmentUrl}
                      title={currentAttachmentName}
                      style={{
                        width: "100%",
                        height: "420px",
                        border: "none",
                        background: "#fff",
                      }}
                    />
                  )}
                </div>
              </div>
            </CustomModal>

            <CustomModal
              show={showSwapTyresModal}
              handleClose={() => setShowSwapTyresModal(false)}
              heading="Swap Tyres"
              size="lg"
              dialogClassName="assets_equipment_swap_modal"
            >
              <div className="assets_equipment_swap_modal_body">
                <div className="assets_equipment_swap_modal_section">
                  <h5>Swap From</h5>

                  <Row>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel("Select Type")}
                        placeholder="Select Type"
                        options={swapTypeOptions}
                        value={findOption(swapTypeOptions, swapTyreForm.sourceType)}
                        onChange={(option) =>
                          handleSwapTypeChange("source", option?.value || "")
                        }
                        isDisabled
                        isSearch={false}
                      />
                    </Col>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel(getSwapAssetFieldLabel(swapTyreForm.sourceType))}
                        placeholder={`Select ${getSwapAssetFieldLabel(swapTyreForm.sourceType)}`}
                        options={sourceAssetOptions}
                        value={findOption(sourceAssetOptions, swapTyreForm.sourceAssetId)}
                        onChange={(option) =>
                          handleSwapAssetChange(
                            "source",
                            String(option?.optionId || option?.id || option?.value || "")
                          )
                        }
                        isDisabled
                        isSearch={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Select Position/Tyre")}
                        placeholder="Select Position / Tyre"
                        options={sourcePositionOptions}
                        value={findOption(sourcePositionOptions, swapTyreForm.sourcePosition)}
                        onChange={(option) =>
                          handleSwapPositionChange("source", option?.value || "")
                        }
                        loading={assetTyresLoading}
                        isSearch={false}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="assets_equipment_swap_modal_section">
                  <h5>Swap To</h5>

                  <Row>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel("Select Type")}
                        placeholder="Select Type"
                        options={swapTypeOptions}
                        value={findOption(swapTypeOptions, swapTyreForm.targetType)}
                        onChange={(option) =>
                          handleSwapTypeChange("target", option?.value || "")
                        }
                        isSearch={false}
                      />
                    </Col>
                    <Col md={6}>
                      <Select
                        label={renderFieldLabel(getSwapAssetFieldLabel(swapTyreForm.targetType))}
                        placeholder={`Select ${getSwapAssetFieldLabel(swapTyreForm.targetType)}`}
                        options={targetAssetOptions}
                        value={findOption(targetAssetOptions, swapTyreForm.targetAssetId)}
                        onChange={(option) =>
                          handleSwapAssetChange(
                            "target",
                            String(option?.optionId || option?.id || option?.value || "")
                          )
                        }
                        isDisabled={!swapTyreForm.targetType}
                        isSearch={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Select Position/Tyre")}
                        placeholder="Select Position / Tyre"
                        options={targetPositionOptions}
                        value={findOption(targetPositionOptions, swapTyreForm.targetPosition)}
                        onChange={(option) =>
                          handleSwapPositionChange("target", option?.value || "")
                        }
                        isDisabled={!swapTyreForm.targetAssetId}
                        loading={targetAssetTyresLoading}
                        isSearch={false}
                      />
                    </Col>
                  </Row>
                </div>

                <div className="assets_equipment_swap_modal_actions">
                  <Button
                    cancelBtn={() => setShowSwapTyresModal(false)}
                    submitBtn={handleSwapTyresUpdate}
                    submitText="Update"
                    loading={swapTyresLoading}
                    type="button"
                    shouldRow={false}
                    disabled={!canSubmitSwapTyres}
                  />
                </div>
              </div>
            </CustomModal>

            <CustomModal
              show={showAddInstallmentModal}
              handleClose={() => setShowAddInstallmentModal(false)}
              heading="Add Installment"
              size="lg"
              dialogClassName="custom_add_installment_modal"
            >
              <div className="assets_equipment_swap_modal_body custom_add_installment_modal_body p-0">
                <div style={{ padding: 0 }}>
                  <Row>
                    <Col md={12}>
                      <div className="assets_add_installment_info_box">
                        <span>Installment:</span> {nextInstallmentNumber}
                      </div>
                    </Col>
                    <Col md={12}>
                      <Select
                        label={renderFieldLabel("Payment Method")}
                        placeholder="Select Payment Method"
                        options={paymentMethodOptions}
                        value={findOption(paymentMethodOptions, installmentForm.paymentMethod)}
                        onChange={(option) =>
                          setInstallmentForm((prev) => {
                            const nextMethod = option?.value || "";
                            if (nextMethod === "bank_transfer") {
                              return {
                                ...prev,
                                paymentMethod: nextMethod,
                              };
                            }

                            return {
                              ...prev,
                              paymentMethod: nextMethod,
                              bankName: "",
                              accountTitle: "",
                              ibanNumber: "",
                              referenceNumber: "",
                            };
                          })
                        }
                        isSearch={false}
                        required={false}
                      />
                    </Col>
                    <Col md={12}>
                      <Input
                        label={renderFieldLabel("Amount")}
                        placeholder="00"
                        type="number"
                        value={installmentForm.amount}
                        onChange={(e) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            amount: e.target.value,
                          }))
                        }
                        required={false}
                        showExtraText
                        extraText="PKR"
                      />
                    </Col>
                    <Col md={12}>
                      <Input
                        label={renderFieldLabel("Submission Date")}
                        type="date"
                        placeholder="DD/MM/YYYY"
                        value={installmentForm.submissionDate}
                        onChange={(e) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            submissionDate: e.target.value,
                          }))
                        }
                        required={false}
                      />
                    </Col>
                    {installmentForm.paymentMethod === "bank_transfer" && (
                      <>
                        <Col md={12}>
                          <Select
                            label={renderFieldLabel("Bank")}
                            placeholder="Select Bank"
                            options={banksDropdownData || []}
                            value={
                              (banksDropdownData || []).find(
                                (option) =>
                                  String(option?.label || option?.name || "").trim() ===
                                  String(installmentForm.bankName || "").trim()
                              ) || null
                            }
                            onChange={(option) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                bankName: option?.label || option?.name || "",
                              }))
                            }
                            isSearch={false}
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("Account Title")}
                            type="text"
                            placeholder="Enter Account Title"
                            value={installmentForm.accountTitle}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                accountTitle: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("IBAN Number")}
                            type="text"
                            placeholder="PK00XXXX"
                            value={installmentForm.ibanNumber}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                ibanNumber: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                        <Col md={12}>
                          <Input
                            label={renderFieldLabel("Reference Number", false)}
                            type="text"
                            placeholder="REF-123456"
                            value={installmentForm.referenceNumber}
                            onChange={(e) =>
                              setInstallmentForm((prev) => ({
                                ...prev,
                                referenceNumber: e.target.value,
                              }))
                            }
                            required={false}
                          />
                        </Col>
                      </>
                    )}
                    <Col md={12}>
                      <div className="parts_general_notes">
                        <h5 className="mb-2">Notes</h5>
                        {/* <p>Add any additional notes or observations here.</p> */}
                        <ReactQuill
                          placeholder={"write notes here..."}
                          modules={modules}
                          theme="snow"
                          value={installmentForm.notes || ""}
                          onChange={(value) =>
                            setInstallmentForm((prev) => ({
                              ...prev,
                              notes: value,
                            }))
                          }
                          formats={formats}
                          className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                        />
                      </div>
                    </Col>
                    <Col md={12} className="mb-3">
                      <FileUpload
                        destination="installment-files"
                        files={installmentForm.files}
                        setFiles={(nextFiles) =>
                          setInstallmentForm((prev) => ({
                            ...prev,
                            files:
                              typeof nextFiles === "function"
                                ? nextFiles(prev.files || [])
                                : nextFiles,
                          }))
                        }
                        maxFileSize={10}
                        maxFiles={5}
                      />
                    </Col>
                  </Row>
                </div>
                <div className="assets_equipment_swap_modal_actions d-flex gap-2 justify-content-end mt-4">
                  <Button
                    cancelBtn={() => setShowAddInstallmentModal(false)}
                    submitBtn={handleAddInstallment}
                    submitText="Add"
                    type="button"
                    shouldRow={false}
                    loading={canUseVehicleInstallmentsApi && createVehicleInstallmentLoading}
                    disabled={!canAddInstallment || isInstallmentCreationDisabled}
                  />
                </div>
              </div>
            </CustomModal>
          </div>
        );
      }}
    </Formik>
  );
};

export default VehicleDetails;

// ================== VEHICLE API MAPPING HELPERS ==================
const toText = (value) => String(value ?? "").trim();

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const numberOrBlank = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : "";
};



const formatNumericText = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "";
  return parsed.toLocaleString("en-US");
};

const formatKm = (value) => `${(toNumber(value) || 0).toLocaleString("en-US")} km`;

const formatDisplayLabel = (value) => {
  const normalized = toText(value);
  if (!normalized) return "";

  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeTyreAssignmentValue = (value) => {
  const normalized = toText(value);
  if (!normalized) return "";

  const lower = normalized.toLowerCase();
  if (lower === "0" || lower === "null" || lower === "n/a") {
    return "";
  }

  return normalized;
};

const formatDurationBetween = (startValue, endValue, { current = false } = {}) => {
  if (current) return "Current";

  const parsedStart = new Date(startValue);
  const parsedEnd = new Date(endValue);
  if (Number.isNaN(parsedStart.getTime()) || Number.isNaN(parsedEnd.getTime())) {
    return "-";
  }

  const diffInMs = parsedEnd.getTime() - parsedStart.getTime();
  if (diffInMs <= 0) return "Same day";

  const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));
  return `${diffInDays} day${diffInDays === 1 ? "" : "s"}`;
};

const findOptionLabelById = (options = [], id) => {
  const normalizedId = Number(id);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return "";

  const matchedOption = (Array.isArray(options) ? options : []).find((option) => {
    const optionId = Number(option?.optionId ?? option?.id ?? option?.value);
    return Number.isFinite(optionId) && optionId === normalizedId;
  });

  return toText(matchedOption?.label || matchedOption?.name || matchedOption?.title);
};

const pickText = (...values) => {
  for (const value of values) {
    const normalized = toText(value);
    if (normalized && normalized !== "-" && normalized !== "--" && normalized !== "N/A") {
      return normalized;
    }
  }

  return "";
};

const resolveVehicleEquipmentLabel = (row, fallbackVehicle = null) =>
  pickText(
    row?.current_equipment?.equipment_id,
    row?.current_equipment?.systemId,
    row?.current_equipment?.serial_number,
    Number(row?.equipment_id) > 0 ? `EQP-${row.equipment_id}` : "",
    fallbackVehicle?.linkedEquipmentId,
    fallbackVehicle?.formData?.linkedEquipmentId
  );

const countAssignedTyres = (assetAxles = []) =>
  (Array.isArray(assetAxles) ? assetAxles : []).reduce((total, axle) => {
    const positions = [
      axle?.outer_left_tyre_id,
      axle?.outer_right_tyre_id,
      axle?.inner_left_tyre_id,
      axle?.inner_right_tyre_id,
    ];

    return total + positions.filter((value) => Boolean(normalizeTyreAssignmentValue(value))).length;
  }, 0);

export const mapVehicleServiceRows = (services = []) =>
  (Array.isArray(services) ? services : []).map((service) => ({
    id: pickText(service?.service_id, service?.id),
    workOrderId:
      pickText(
        service?.work_order?.work_order_id,
        service?.work_order?.id,
        service?.work_order_id,
        service?.workOrderId,
        "-"
      ) || "-",
    serviceType:
      pickText(service?.services_type, service?.service_type, service?.serviceType)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase()) || "-",
    serviceDate:
      formatDateOnly(service?.start_at || service?.complete_at || service?.service_date) || "-",
    odometer: formatNumericText(service?.odo_meter) || "-",
    partCost: toNumber(service?.cost_parts),
    labourCost: toNumber(service?.cost_labor),
    totalCost: toNumber(service?.complete_total || service?.sub_total),
    status: normalizeVehicleStatusValue(service?.status || "completed") || "completed",
  }));

export const mapVehicleTripRows = (trips = []) =>
  (Array.isArray(trips) ? trips : []).map((trip) => ({
    tripId: pickText(trip?.trip_id, trip?.id),
    driver: pickText(
      trip?.primary_driver?.username,
      trip?.primary_driver?.name,
      trip?.driver?.username,
      trip?.driver?.name,
      "-"
    ) || "-",
    driverImage:
      trip?.primary_driver?.image ||
      trip?.driver?.image ||
      "",
    dueDate: formatDateOnly(trip?.start_at || trip?.actual_start_at) || "-",
    endDate: formatDateOnly(trip?.actual_end_at || trip?.expected_end_at) || "-",
    distance: formatNumericText(trip?.distance ?? trip?.covered_distance) || "-",
    fuelUsed: formatNumericText(trip?.fuel_used ?? trip?.fuelUsed) || "-",
  }));

export const mapVehicleFuelLogRows = (fuelLogs = [], { stationOptions = [] } = {}) =>
  (Array.isArray(fuelLogs) ? fuelLogs : []).map((fuelLog) => ({
    logId:
      pickText(
        fuelLog?.fuel_log_id,
        fuelLog?.log_id,
        Number(fuelLog?.id) > 0 ? `F-LOG-${fuelLog.id}` : "",
        fuelLog?.id
      ) || "-",
    date: formatDateOnly(fuelLog?.refuel_at || fuelLog?.created_at) || "-",
    odometer: formatNumericText(fuelLog?.odo_meter || fuelLog?.odometer) || "-",
    litres:
      formatNumericText(
        fuelLog?.claimed_fueled ?? fuelLog?.fuel_quantity ?? fuelLog?.litres
      ) || "-",
    cost: toNumber(fuelLog?.total_fuel_cost || fuelLog?.cost),
    supplier:
      pickText(
        fuelLog?.fuel_station?.fuel_station_name,
        fuelLog?.fuel_station?.name,
        fuelLog?.fuel_station?.title,
        findOptionLabelById(stationOptions, fuelLog?.fuel_station_id),
        fuelLog?.supplier_name,
        fuelLog?.supplier,
        Number(fuelLog?.fuel_station_id) > 0
          ? `Fuel Station ${fuelLog.fuel_station_id}`
          : "-"
      ) || "-",
  }));

export const mapVehicleEquipmentHistoryRows = (
  history = [],
  { equipmentOptions = [] } = {}
) =>
  (Array.isArray(history) ? history : []).map((item) => {
    const startDate = formatDateOnly(
      item?.start_date || item?.installed_at || item?.created_at
    );
    const endDate = formatDateOnly(item?.end_date || item?.removed_at);

    return {
      equipmentId:
        pickText(
          item?.equipment?.equipment_id,
          item?.equipment?.serial_number,
          findOptionLabelById(equipmentOptions, item?.equipment_id),
          item?.equipment_id,
          item?.equipmentId,
          item?.serial_number
        ) || "-",
      startDate: startDate || "-",
      endDate: endDate || "-",
      duration:
        pickText(item?.duration, item?.duration_text, item?.tenure) ||
        formatDurationBetween(item?.installed_at, item?.removed_at, {
          current: Boolean(item?.current) && !item?.removed_at,
        }),
    };
  });

export const mapVehicleTyreHistoryRows = (
  history = [],
  { tyreOptions = [] } = {}
) =>
  (Array.isArray(history) ? history : []).map((item) => ({
    tyreId:
      pickText(
        item?.tyre?.tyres_id,
        item?.tyre?.system_id,
        item?.tyres_id,
        findOptionLabelById(tyreOptions, item?.tyre?.id),
        findOptionLabelById(tyreOptions, item?.tyre_id),
        item?.tyre_id,
        item?.tyreId
      ) || "-",
    position:
      pickText(
        item?.position,
        item?.installed_position,
        item?.position_name,
        item?.mount_position,
        item?.current_position
      ) || "-",
    installedDate:
      formatDateOnly(item?.installed_at || item?.installed_date || item?.start_date || item?.created_at) || "-",
    removedDate: formatDateOnly(item?.removed_at || item?.removed_date || item?.end_date) || "-",
    startOdometer:
      formatNumericText(
        item?.start_odometer ??
          item?.installed_odometer ??
          item?.start_odm ??
          item?.installed_odm ??
          item?.start_odo_meter
      ) || "-",
    endOdometer:
      formatNumericText(
        item?.end_odometer ??
          item?.removed_odometer ??
          item?.end_odm ??
          item?.removed_odm ??
          item?.end_odo_meter
      ) || "-",
    kmDriven: formatNumericText(item?.km_driven ?? item?.distance_driven ?? item?.distance) || "-",
  }));

export const mapVehicleInstallmentRows = (installments = []) =>
  (Array.isArray(installments) ? installments : []).map((item, index) => ({
    number: String(
      item?.order_number ||
      item?.installment_number ||
      item?.installment_no ||
      item?.number ||
      index + 1
    ).padStart(2, "0"),
    paymentMethod: formatDisplayLabel(
      pickText(item?.payment_method, item?.paymentMethod)
    ),
    amount: toNumber(item?.installment_amount || item?.amount),
    submissionDate: formatDateOnly(
      item?.submit_at ||
      item?.first_payment_date ||
      item?.submission_date
    ),
    dueDate: formatDateOnly(
      item?.submit_at ||
      item?.first_payment_date ||
      item?.due_date
    ),
    attachmentsCount:
      (Array.isArray(item?.installment_files) ? item.installment_files.length : 0) ||
      (Array.isArray(item?.files) ? item.files.length : 0),
    attachments: Array.isArray(item?.installment_files)
      ? item.installment_files
      : Array.isArray(item?.files)
        ? item.files
        : [],
    status: normalizeVehicleStatusValue(item?.status || "pending") || "pending",
  }));

const mapAxleConfigurationsFromApi = (assetAxles = []) =>
  (Array.isArray(assetAxles) ? assetAxles : []).map((axle, index) => {
    const hasInnerTyres = Boolean(
      normalizeTyreAssignmentValue(axle?.inner_left_tyre_id) ||
      normalizeTyreAssignmentValue(axle?.inner_right_tyre_id)
    );

    return {
      id: axle?.id || "",
      sourceId: axle?.source_id || "",
      sourceType: axle?.source_type || "",
      axleId: toText(axle?.name || `A${index + 1}`) || `A${index + 1}`,
      tyreConfiguration: hasInnerTyres ? "dual" : "single",
      assignments: {
        left: normalizeTyreAssignmentValue(axle?.outer_left_tyre_id),
        right: normalizeTyreAssignmentValue(axle?.outer_right_tyre_id),
        leftInner: normalizeTyreAssignmentValue(axle?.inner_left_tyre_id),
        leftOuter: normalizeTyreAssignmentValue(axle?.outer_left_tyre_id),
        rightInner: normalizeTyreAssignmentValue(axle?.inner_right_tyre_id),
        rightOuter: normalizeTyreAssignmentValue(axle?.outer_right_tyre_id),
      },
    };
  });

export const mapVehicleAxleConfigurations = (assetAxles = []) =>
  mapAxleConfigurationsFromApi(assetAxles);

export const resolveVehicleDetailId = (routeValue, stateVehicle = null) => {
  const candidates = [
    stateVehicle?.id,
    stateVehicle?.vehicle_id,
    stateVehicle?.systemId,
    routeValue,
  ];

  for (const candidate of candidates) {
    const directNumber = Number(candidate);
    if (Number.isFinite(directNumber) && directNumber > 0) {
      return directNumber;
    }

    const normalized = String(candidate || "").trim();
    if (/^\d+$/.test(normalized)) {
      const parsed = Number(normalized);
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }

    if (/^ve-\d+$/i.test(normalized)) {
      const parsed = Number(normalized.replace(/[^\d]/g, ""));
      if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
      }
    }
  }

  return null;
};

export const mapVehicleDetailRecord = (
  row,
  {
    fallbackVehicle = null,
    makeOptions = [],
    modelOptions = [],
    transmissionOptions = [],
    branchOptions = [],
  } = {}
) => {
  if (!row || typeof row !== "object") return null;

  const systemId = pickText(
    row?.vehicle_id,
    row?.systemId,
    fallbackVehicle?.systemId,
    fallbackVehicle?.vehicle_id
  );
  if (!systemId) return null;

  const purchaseList = Array.isArray(row?.asset_purchases)
    ? row.asset_purchases
    : row?.asset_purchases
      ? [row.asset_purchases]
      : [];
  const purchase = purchaseList[0] || {};
  const insuranceList = Array.isArray(row?.asset_insurances)
    ? row.asset_insurances
    : row?.asset_insurances
      ? [row.asset_insurances]
      : [];
  const insurance = insuranceList[0] || {};
  const installmentList = Array.isArray(row?.asset_installments)
    ? row.asset_installments
    : row?.asset_installments
      ? [row.asset_installments]
      : [];
  const serviceRecords = mapVehicleServiceRows(row?.services);
  const maintenanceCost = serviceRecords.reduce(
    (total, service) => total + toNumber(service?.totalCost),
    0
  );
  const currentOdometerValue = toNumber(
    row?.current_info?.odm ?? row?.odm ?? fallbackVehicle?.currentOdometerValue,
    0
  );
  const make = pickText(
    row?.vehicle_make?.name,
    row?.vehicle_make?.title,
    row?.make?.name,
    findOptionLabelById(makeOptions, row?.make_id),
    fallbackVehicle?.formData?.make,
    fallbackVehicle?.make
  );
  const model = pickText(
    row?.vehicle_model?.name,
    row?.vehicle_model?.title,
    row?.model?.name,
    findOptionLabelById(modelOptions, row?.model_id),
    fallbackVehicle?.formData?.model,
    fallbackVehicle?.model
  );
  const vehicleType = pickText(
    row?.vehicle_type?.title,
    row?.vehicle_type?.name,
    row?.type?.title,
    row?.type?.name,
    fallbackVehicle?.formData?.vehicleType,
    fallbackVehicle?.vehicleType
  );
  const transmissionType = pickText(
    row?.transmission?.name,
    row?.transmission?.title,
    findOptionLabelById(transmissionOptions, row?.transmission_type),
    fallbackVehicle?.formData?.transmissionType,
    fallbackVehicle?.transmissionType
  );
  const branch = pickText(
    row?.branch?.name,
    row?.branch?.title,
    findOptionLabelById(branchOptions, row?.branch_id),
    fallbackVehicle?.formData?.branch,
    fallbackVehicle?.branch
  );
  const ownershipType = pickText(
    row?.ownership_type,
    row?.ownershipType,
    fallbackVehicle?.ownership_type,
    fallbackVehicle?.ownershipTypeRaw,
    fallbackVehicle?.formData?.ownershipTypeRaw,
    fallbackVehicle?.ownershipType,
    fallbackVehicle?.formData?.ownershipType
  );
  const linkedEquipmentId = resolveVehicleEquipmentLabel(row, fallbackVehicle);
  const axleConfigurations = mapAxleConfigurationsFromApi(row?.asset_axles);
  const tyreHistory = mapVehicleTyreHistoryRows(row?.tyre_installed_history);
  const operationalSummary = {
    totalKm: currentOdometerValue,
    averageKmPerMonth: toNumber(row?.current_info?.average_km_per_month),
    last30DaysKm: toNumber(row?.current_info?.last_30_days_km),
  };

  return {
    id: row?.id || fallbackVehicle?.id,
    systemId,
    registrationNumber: pickText(
      row?.registration_number,
      fallbackVehicle?.registrationNumber,
      fallbackVehicle?.formData?.registrationNumber
    ),
    vinChassisNo: pickText(
      row?.vin,
      row?.chassis_number,
      fallbackVehicle?.vinChassisNo,
      fallbackVehicle?.formData?.vinChassisNo
    ),
    imeino: pickText(
      row?.imeino,
      fallbackVehicle?.formData?.imeino,
      fallbackVehicle?.imeino
    ),
    trackingPackage: pickText(
      row?.tracking_package,
      fallbackVehicle?.formData?.trackingPackage,
      fallbackVehicle?.formData?.tracking_package,
      fallbackVehicle?.trackingPackage,
      fallbackVehicle?.tracking_package
    ),
    make,
    model,
    vehicleType,
    vehicleTypeId: getPositiveNumber(
      row?.vehicle_type_id,
      row?.vehicle_type?.id,
      row?.type?.id,
      fallbackVehicle?.formData?.vehicleTypeId,
      fallbackVehicle?.vehicleTypeId
    ) || "",
    ownershipType,
    ownershipTypeRaw: ownershipType,
    transporterName: pickText(
      row?.transporter?.name,
      row?.transporter?.label,
      row?.transporter_name,
      row?.threePlTransporter?.name,
      row?.vehicle_3pl?.transporter?.name,
      row?.vehicle_hired?.transporter?.name,
      fallbackVehicle?.formData?.transporterName
    ),
    loadCapacity: numberOrBlank(
      row?.load_capacity ??
        row?.loadCapacity ??
        row?.vehicle_3pl?.load_capacity ??
        row?.vehicle_hired?.load_capacity ??
        fallbackVehicle?.formData?.loadCapacity
    ) || "",
    driverName: pickText(
      row?.hired_vehicle_driver?.name,
      row?.driver_name,
      row?.driverName,
      row?.vehicle_3pl?.driver_name,
      row?.vehicle_hired?.driver_name,
      fallbackVehicle?.formData?.driverName
    ),
    mobileNumber: pickText(
      row?.hired_vehicle_driver?.mobile_no,
      row?.mobile_no,
      row?.mobileNo,
      row?.contact,
      row?.vehicle_3pl?.mobile_no,
      row?.vehicle_hired?.mobile_no,
      fallbackVehicle?.formData?.mobileNumber
    ),
    cnic: pickText(
      row?.hired_vehicle_driver?.cnic,
      row?.cnic,
      row?.vehicle_3pl?.cnic,
      row?.vehicle_hired?.cnic,
      fallbackVehicle?.formData?.cnic
    ),
    year:
      numberOrBlank(
        row?.manufacturing_year ?? fallbackVehicle?.year ?? fallbackVehicle?.formData?.year
      ) || "",
    branch,
    assignedDriver: {
      id:
        row?.hired_vehicle_driver?.id ||
        row?.driver?.id ||
        row?.assigned_driver?.id ||
        fallbackVehicle?.assignedDriver?.id ||
        fallbackVehicle?.formData?.assignedDriverId ||
        "",
      name: pickText(
        row?.hired_vehicle_driver?.name,
        row?.driver?.name,
        row?.driver?.label,
        row?.assigned_driver?.name,
        row?.assigned_driver?.label,
        fallbackVehicle?.assignedDriver?.name,
        fallbackVehicle?.formData?.assignedDriver
      ),
      mobileNumber: pickText(
        row?.hired_vehicle_driver?.mobile_no,
        row?.driver?.mobile_no,
        row?.driver?.mobileNumber
      ),
      cnic: pickText(
        row?.hired_vehicle_driver?.cnic,
        row?.driver?.cnic
      ),
      image: row?.driver?.image || row?.assigned_driver?.image || fallbackVehicle?.assignedDriver?.image || "",
    },
    linkedEquipmentId,
    linkedTyres:
      countAssignedTyres(row?.asset_axles) ||
      (Array.isArray(row?.current_tyres) ? row.current_tyres.length : 0) ||
      tyreHistory.length,
    currentOdometerValue,
    currentOdometer: formatKm(currentOdometerValue),
    nextServiceDue: formatDateOnly(row?.next_service_due || row?.nextServiceDue) || "-",
    status: normalizeVehicleStatusValue(
      row?.status || fallbackVehicle?.status || fallbackVehicle?.formData?.status || "active"
    ),
    totalCost: toNumber(purchase?.purchase_cost || fallbackVehicle?.totalCost),
    description: row?.description || fallbackVehicle?.description || fallbackVehicle?.formData?.description || "",
    notes: row?.notes || fallbackVehicle?.notes || fallbackVehicle?.formData?.notes || "",
    hired_vehicle_driver: row?.hired_vehicle_driver || fallbackVehicle?.hired_vehicle_driver || null,
    files:
      extractVehicleFiles(row?.files).length > 0
        ? extractVehicleFiles(row?.files)
        : extractVehicleFiles(row).length > 0
        ? extractVehicleFiles(row)
        : extractVehicleFiles(fallbackVehicle),
    attachments:
      extractVehicleFiles(row?.files).length > 0
        ? extractVehicleFiles(row?.files)
        : extractVehicleFiles(row).length > 0
        ? extractVehicleFiles(row)
        : extractVehicleFiles(fallbackVehicle),
    formData: {
      ...(fallbackVehicle?.formData || {}),
      registrationNumber: pickText(
        row?.registration_number,
        fallbackVehicle?.formData?.registrationNumber,
        fallbackVehicle?.registrationNumber
      ),
      vinChassisNo: pickText(
        row?.vin,
        row?.chassis_number,
        fallbackVehicle?.formData?.vinChassisNo,
        fallbackVehicle?.vinChassisNo
      ),
      make,
      model,
      vehicleType,
      vehicleTypeId:
        row?.vehicle_type_id ||
        row?.vehicle_type?.id ||
        row?.type?.id ||
        fallbackVehicle?.formData?.vehicleTypeId ||
        "",
      ownershipType,
      ownershipTypeRaw: ownershipType,
      transporter:
        row?.transporter_id ||
        row?.transporter?.id ||
        row?.three_pl_transporter_id ||
        row?.threePlTransporter?.id ||
        row?.vehicle_3pl?.transporter_id ||
        row?.vehicle_hired?.transporter_id ||
        fallbackVehicle?.formData?.transporter ||
        "",
      transporterName: pickText(
        row?.transporter?.name,
        row?.transporter?.label,
        row?.transporter_name,
        row?.threePlTransporter?.name,
        row?.vehicle_3pl?.transporter?.name,
        row?.vehicle_hired?.transporter?.name,
        fallbackVehicle?.formData?.transporterName
      ),
      loadCapacity:
        numberOrBlank(
          row?.load_capacity ??
            row?.loadCapacity ??
            row?.vehicle_3pl?.load_capacity ??
            row?.vehicle_hired?.load_capacity ??
            fallbackVehicle?.formData?.loadCapacity
        ) || "",
      driverName: pickText(
        row?.hired_vehicle_driver?.name,
        row?.driver_name,
        row?.driverName,
        row?.vehicle_3pl?.driver_name,
        row?.vehicle_hired?.driver_name,
        fallbackVehicle?.formData?.driverName
      ),
      mobileNumber: pickText(
        row?.hired_vehicle_driver?.mobile_no,
        row?.mobile_no,
        row?.mobileNo,
        row?.contact,
        row?.vehicle_3pl?.mobile_no,
        row?.vehicle_hired?.mobile_no,
        fallbackVehicle?.formData?.mobileNumber
      ),
      cnic: pickText(
        row?.hired_vehicle_driver?.cnic,
        row?.cnic,
        row?.vehicle_3pl?.cnic,
        row?.vehicle_hired?.cnic,
        fallbackVehicle?.formData?.cnic
      ),
      hired_vehicle_driver: row?.hired_vehicle_driver || fallbackVehicle?.formData?.hired_vehicle_driver || null,
      year:
        numberOrBlank(
          row?.manufacturing_year ?? fallbackVehicle?.formData?.year ?? fallbackVehicle?.year
        ) || "",
      fuelType: toText(row?.fuel_type).toLowerCase() || fallbackVehicle?.formData?.fuelType || "",
      fuelTankType: toText(row?.fuel_tank_type || fallbackVehicle?.formData?.fuelTankType),
      tankCapacity:
        numberOrBlank(row?.tank_capacity ?? fallbackVehicle?.formData?.tankCapacity) || "",
      currentFuelEfficiency:
        numberOrBlank(
          row?.initial_fuel_efficiency ?? fallbackVehicle?.formData?.currentFuelEfficiency
        ) || "",
      odometerUnit: fallbackVehicle?.formData?.odometerUnit || "km",
      transmissionType,
      engineCapacity:
        numberOrBlank(row?.engine_capacity ?? fallbackVehicle?.formData?.engineCapacity) || "",
      imeino: pickText(
        row?.imeino,
        fallbackVehicle?.formData?.imeino,
        fallbackVehicle?.imeino
      ),
      trackingPackage: pickText(
        row?.tracking_package,
        fallbackVehicle?.formData?.trackingPackage,
        fallbackVehicle?.formData?.tracking_package,
        fallbackVehicle?.trackingPackage,
        fallbackVehicle?.tracking_package
      ),
      assignedDriver: pickText(
        row?.driver?.name,
        row?.driver?.label,
        row?.assigned_driver?.name,
        row?.assigned_driver?.label,
        fallbackVehicle?.formData?.assignedDriver,
        fallbackVehicle?.assignedDriver?.name
      ),
      assignedDriverId:
        row?.driver?.id ||
        row?.assigned_driver?.id ||
        fallbackVehicle?.formData?.assignedDriverId ||
        fallbackVehicle?.assignedDriver?.id ||
        "",
      branch,
      status: normalizeVehicleStatusValue(
        row?.status || fallbackVehicle?.formData?.status || fallbackVehicle?.status || "active"
      ),
      linkedEquipmentId,
      numberOfAxles:
        String(
          toNumber(
            row?.number_of_axles ??
              fallbackVehicle?.formData?.numberOfAxles ??
              fallbackVehicle?.numberOfAxles,
            0
          )
        ) || "0",
      axleConfigurations,
      description:
        row?.description ||
        fallbackVehicle?.formData?.description ||
        fallbackVehicle?.description ||
        "",
      purchaseDate: formatDateOnly(purchase?.purchase_at),
      purchaseCost: numberOrBlank(purchase?.purchase_cost) || "",
      paymentType: pickText(
        normalizePaymentType(purchase?.payment_type, purchase?.payment_method),
        fallbackVehicle?.formData?.paymentType
      ),
      paymentMethod: pickText(
        normalizePaymentMethod(purchase?.payment_method, purchase?.payment_type),
        fallbackVehicle?.formData?.paymentMethod
      ),
      depreciationMethod: pickText(
        purchase?.depreciation_method,
        fallbackVehicle?.formData?.depreciationMethod
      ),
      usefulLife: numberOrBlank(purchase?.useful_life) || "",
      residualValue: numberOrBlank(purchase?.residual_value) || "",
      policyNumber:
        pickText(
          insurance?.policy_number,
          insurance?.policyNumber,
          fallbackVehicle?.formData?.policyNumber
        ) || "",
      insuranceProvider:
        pickText(
          insurance?.insurance_provider,
          insurance?.provider,
          fallbackVehicle?.formData?.insuranceProvider
        ) || "",
      coverageType:
        pickText(
          insurance?.coverage_type,
          insurance?.coverageType,
          fallbackVehicle?.formData?.coverageType
        ) || "",
      coverageAmount:
        numberOrBlank(insurance?.coverage_amount ?? fallbackVehicle?.formData?.coverageAmount) ||
        "",
      premiumAmount:
        numberOrBlank(insurance?.premium_amount ?? fallbackVehicle?.formData?.premiumAmount) ||
        "",
      insuranceStartDate:
        formatDateOnly(
          insurance?.insurance_start_at ||
            insurance?.start_at ||
            insurance?.start_date ||
            fallbackVehicle?.formData?.insuranceStartDate
        ) || "",
      insuranceExpiryDate:
        formatDateOnly(
          insurance?.insurance_expire_at ||
            insurance?.expire_at ||
            insurance?.expiry_date ||
            fallbackVehicle?.formData?.insuranceExpiryDate
        ) || "",
      insuranceStatus:
        pickText(
          insurance?.insurance_status,
          insurance?.status,
          fallbackVehicle?.formData?.insuranceStatus
        ) || "",
      insuranceCost:
        numberOrBlank(insurance?.premium_amount ?? fallbackVehicle?.formData?.insuranceCost) || "",
      maintenanceCost:
        numberOrBlank(fallbackVehicle?.formData?.maintenanceCost) || maintenanceCost || "",
      notes: row?.notes || fallbackVehicle?.formData?.notes || fallbackVehicle?.notes || "",
      files:
        extractVehicleFiles(row?.files).length > 0
          ? extractVehicleFiles(row?.files)
          : extractVehicleFiles(row).length > 0
          ? extractVehicleFiles(row)
          : extractVehicleFiles(fallbackVehicle),
      attachments:
        extractVehicleFiles(row?.files).length > 0
          ? extractVehicleFiles(row?.files)
          : extractVehicleFiles(row).length > 0
          ? extractVehicleFiles(row)
          : extractVehicleFiles(fallbackVehicle),
      driverDocument:
        extractVehicleFiles(row?.files).length > 0
          ? extractVehicleFiles(row?.files)
          : extractVehicleFiles(row).length > 0
          ? extractVehicleFiles(row)
          : extractVehicleFiles(fallbackVehicle),
      serviceRecords,
      equipmentHistory: mapVehicleEquipmentHistoryRows(row?.equipment_installed_history),
      tyreHistory,
      installmentSchedule: mapVehicleInstallmentRows(installmentList),
      tripHistory: Array.isArray(fallbackVehicle?.formData?.tripHistory)
        ? fallbackVehicle.formData.tripHistory
        : [],
      fuelLogs: Array.isArray(fallbackVehicle?.formData?.fuelLogs)
        ? fallbackVehicle.formData.fuelLogs
        : [],
      operationalSummary,
      auditSnapshot: fallbackVehicle?.formData?.auditSnapshot || {},
      comments: Array.isArray(fallbackVehicle?.formData?.comments)
        ? fallbackVehicle.formData.comments
        : [],
      activities: Array.isArray(fallbackVehicle?.formData?.activities)
        ? fallbackVehicle.formData.activities
        : [],
    },
  };
};

const normalizePaymentValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

const paymentMethodValues = ["bank_transfer", "cash", "lease", "financing"];

const normalizePaymentType = (value, paymentMethod = "") => {
  const normalizedValue = normalizePaymentValue(value);
  const normalizedMethod = normalizePaymentValue(paymentMethod);

  if (normalizedValue === "full_payment" || normalizedValue === "installments") {
    return normalizedValue;
  }

  if (
    normalizedValue === "installment" ||
    normalizedMethod === "installments" ||
    normalizedMethod === "installment"
  ) {
    return "installments";
  }

  if (
    paymentMethodValues.includes(normalizedValue) ||
    paymentMethodValues.includes(normalizedMethod)
  ) {
    return "full_payment";
  }

  return "";
};

const normalizePaymentMethod = (value, paymentType = "") => {
  const normalizedValue = normalizePaymentValue(value);
  const normalizedType = normalizePaymentValue(paymentType);

  if (paymentMethodValues.includes(normalizedValue)) {
    return normalizedValue;
  }

  if (paymentMethodValues.includes(normalizedType)) {
    return normalizedType;
  }

  return "";
};
